// Single source of truth for the /alternatives hub and every /alternatives/:platform page.
// Pure data (no React or path-alias imports) so the crawler renderer, sitemap and llms.txt
// in /api can import it too.
//
// Evidence policy: competitor facts come only from the vendor's own website and are dated.
// Where a vendor does not publish something, say so and tell the reader to confirm.

const SITE = 'https://beatview.ai';

export const ALTERNATIVES_REVIEWED = '2026-09-15';
export const ALTERNATIVES_REVIEWED_LABEL = '15 September 2026';
/** When the /alternatives/:platform pages were first published. */
export const ALTERNATIVES_FIRST_PUBLISHED = '2026-02-07';

export type ComparisonKey = 'focus' | 'resume' | 'video' | 'ai' | 'assessments' | 'clientSharing' | 'integrations' | 'pricing' | 'freePlan';

export const COMPARISON_AREAS: { key: ComparisonKey; label: string }[] = [
  { key: 'focus', label: 'Primary focus' },
  { key: 'resume', label: 'Resume screening' },
  { key: 'video', label: 'Video interviews' },
  { key: 'ai', label: 'AI capabilities' },
  { key: 'assessments', label: 'Assessments' },
  { key: 'clientSharing', label: 'Agency client sharing' },
  { key: 'integrations', label: 'ATS and API' },
  { key: 'pricing', label: 'Published pricing' },
  { key: 'freePlan', label: 'Free plan or trial' },
];

export interface Source {
  label: string;
  url: string;
}

export interface Platform {
  id: string;
  name: string;
  website: string;
  /** Short category label, e.g. "One-way video interviews". */
  category: string;
  bestFor: string;
  summary: string;
  interviewFormats: string;
  aiSummary: string;
  startingPrice: string;
  strengths: string[];
  watchOuts: string[];
  facts: Record<ComparisonKey, string>;
  /** Set when the platform has a free plan or free trial worth naming in FAQs. */
  freeNote?: string;
  sources: Source[];
  isBeatview?: boolean;
  /** ISO date this vendor's pages were last read, when later than ALTERNATIVES_REVIEWED. */
  reviewed?: string;
}

export interface Faq {
  question: string;
  answer: string;
}

export interface CompetitorPage {
  id: string;
  name: string;
  /** Alternatives in display order. Beatview is always first and disclosed as our product. */
  alternativeIds: string[];
  overview: string;
  quickAnswer: string;
  switchTriggers: { title: string; body: string }[];
  difference: string;
  chooseBeatview: string[];
  keepCompetitor: string[];
  buyerQuestions: string[];
  ctaHeadline: string;
  /** Our pricing guide for the compared platform, when one exists. */
  pricingHref?: string;
}

export const BEATVIEW: Platform = {
  id: 'beatview',
  name: 'Beatview',
  website: 'https://platform.beatview.ai',
  category: 'AI recruiting agent for agencies',
  bestFor: 'Recruitment agencies that want application screening, structured one-way AI video interviews and client sharing in one workflow',
  summary: 'Beatview screens applications against the criteria a consultant configures, runs timed one-way video interviews, scores each answer with the reasoning visible, and lets agencies share candidates with clients through password-protected links. Recruiters make every decision.',
  interviewFormats: 'Timed one-way video',
  aiSummary: 'Resume job-match scores with reasons; per-dimension interview scoring and ranking',
  startingPrice: 'Free; Pro $179/mo billed annually',
  strengths: [
    'Resume screening, one-way AI video interviews and a Work Style assessment in one workflow',
    'Every score shows the evidence behind it, and recruiters keep the decision',
    'Password-protected candidate sharing with external client notes',
    'Published pricing with a free plan for one active job',
  ],
  watchOuts: [
    'AI video interviews and Work Style assessments require Pro',
    'Built for asynchronous interviews, not live video calls',
    'Zapier, API access and webhooks are on Enterprise',
  ],
  facts: {
    focus: 'Agency workflow from application screening to structured AI video interview and client review',
    resume: 'AI job-match score with reasons, filters and Resume Bank matching',
    video: 'Timed, structured one-way video interviews (Pro and Enterprise)',
    ai: 'Per-dimension answer scoring and ranked completed interviews; recruiters make every decision',
    assessments: 'Work Style assessment across six dimensions (Pro and Enterprise)',
    clientSharing: 'Password-protected candidate links with external client notes',
    integrations: 'Most ATS platforms through Zapier; native integrations on request; LinkedIn apply links; API and webhooks on Enterprise',
    pricing: 'Free; Pro $219/month or $179/month billed annually ($2,148/year); Enterprise quoted',
    freePlan: 'Free plan: one active job with unlimited candidates (AI video interviews on Pro)',
  },
  freeNote: 'Beatview has a Free plan with one active job and unlimited candidates for that job, including resume screening and a branded career page; AI video interviews are included on Pro',
  sources: [
    { label: 'Beatview pricing', url: `${SITE}/pricing` },
    { label: 'Beatview AI interviews', url: `${SITE}/ai-interviews` },
  ],
  isBeatview: true,
};

export const PLATFORMS: Record<string, Platform> = {
  beatview: BEATVIEW,
  hirevue: {
    id: 'hirevue',
    name: 'HireVue',
    website: 'https://www.hirevue.com',
    category: 'Enterprise interviewing and assessments',
    bestFor: 'Large enterprises that want video interviews, an AI Interviewer, assessments and scheduling from one vendor',
    summary: 'HireVue is an enterprise hiring platform combining on-demand and live video interviewing, a conversational AI Interviewer, assessments with an assessment builder, automated scheduling and text-based candidate engagement.',
    interviewFormats: 'On-demand (one-way) and live video; conversational AI Interviewer',
    aiSummary: 'Conversational AI Interviewer, assessments, text automation',
    startingPrice: 'Not published (quote)',
    strengths: [
      'Live and on-demand video interviews in one platform',
      'Assessments and an assessment builder alongside interviews',
      'Automated interview scheduling and conversational text automation',
      'Support offered in 20+ languages',
    ],
    watchOuts: [
      'Pricing is not published, so budgeting requires a sales process',
      'Suite breadth can mean a longer evaluation for smaller teams',
      'Confirm how AI is used in evaluation and what candidate notice is required where you hire',
    ],
    facts: {
      focus: 'Enterprise suite: video interviewing, AI Interviewer, assessments and scheduling',
      resume: 'Match and apply tools listed; confirm resume screening scope with HireVue',
      video: 'On-demand (one-way) and live video interviews',
      ai: 'Conversational AI Interviewer, assessments and text automation',
      assessments: 'Assessments and Assessment Builder',
      clientSharing: 'Not positioned around agency client sharing; confirm with HireVue',
      integrations: 'ATS partner ecosystem; confirm your ATS with HireVue',
      pricing: 'Not published; request a quote',
      freePlan: 'No free plan listed',
    },
    sources: [
      { label: 'HireVue platform', url: 'https://www.hirevue.com/platform' },
      { label: 'HireVue pricing', url: 'https://www.hirevue.com/pricing' },
    ],
  },
  sparkhire: {
    id: 'sparkhire',
    name: 'Spark Hire',
    website: 'https://www.sparkhire.com',
    category: 'One-way and live video interviews',
    bestFor: 'Small and mid-size in-house teams that want one-way and live video, with an optional ATS and assessments',
    summary: 'Spark Hire offers one-way and live video interviews with transcription, AI summaries and video scoring, and sells a behavioral assessment, automated reference checks and an applicant tracking system (Spark Hire Recruit) with AI resume review.',
    interviewFormats: 'One-way and live video',
    aiSummary: 'Transcription, AI summaries, video scoring; AI resume review in Recruit ATS',
    startingPrice: '$249/mo billed annually',
    strengths: [
      'One-way and live video interviews on the same plan',
      'Transcription, AI summaries and video scoring included',
      '40+ ATS integrations, 50+ HR partners and Zapier',
      'Optional behavioral assessment, reference checks and ATS',
    ],
    watchOuts: [
      'The monthly video plan is limited to five jobs and five users',
      'Video interviews, assessments and the ATS are priced as separate products',
      'Confirm the contract length required for annual plans',
    ],
    facts: {
      focus: 'Video interviews, with behavioral assessments, reference checks and an ATS as separate products',
      resume: 'AI Resume Review in Spark Hire Recruit ATS plans (6,000–36,000 reviews a year)',
      video: 'One-way and live video interviews',
      ai: 'Transcription, AI summaries and video scoring',
      assessments: 'Behavioral assessment ($249–$299/month as a separate product)',
      clientSharing: 'Collaborative team review; confirm external client-sharing workflow',
      integrations: '40+ ATS integrations, 50+ HR partners and Zapier',
      pricing: 'Video interviews $299/month (monthly, 5 jobs, 5 users) or $249/month billed annually; Recruit ATS from $335/month billed annually',
      freePlan: 'No free plan listed',
    },
    sources: [
      { label: 'Spark Hire pricing', url: 'https://www.sparkhire.com/pricing' },
      { label: 'Spark Hire video interviews', url: 'https://www.sparkhire.com/video-interviews/' },
    ],
  },
  testgorilla: {
    id: 'testgorilla',
    name: 'TestGorilla',
    website: 'https://www.testgorilla.com',
    category: 'Pre-employment testing',
    bestFor: 'Teams whose main requirement is a broad library of skills, cognitive and personality tests',
    summary: 'TestGorilla is a pre-employment testing platform with 350+ tests, resume scoring and qualifying questions. Its Plus plan adds AI interviews and video questions with AI scoring, job simulations, custom coding challenges, and ATS and API access.',
    interviewFormats: 'AI interviews and video questions (Plus)',
    aiSummary: 'AI scoring of interviews and video questions; resume scoring',
    startingPrice: 'Free; Core $142/mo',
    strengths: [
      'Library of 350+ tests on Core and above',
      'Free plan with 10 credits a month',
      'Job simulations and custom coding challenges on Plus',
      'Resume scoring and qualifying questions on every plan',
    ],
    watchOuts: [
      'AI interviews, video questions with AI scoring, and ATS or API access are listed on Plus (from $400/month)',
      'Usage is credit-based; AI interviews use two credits per candidate',
      'Confirm how candidates are shared with agency clients',
    ],
    facts: {
      focus: 'Pre-employment test library with AI interviews and video questions',
      resume: 'Resume scoring and qualifying questions on all plans',
      video: 'AI interviews and video questions with AI scoring listed on Plus',
      ai: 'AI scoring of interview and video responses (Plus)',
      assessments: '350+ tests on Core and above; job simulations and custom coding on Plus',
      clientSharing: 'Confirm agency client-sharing workflow with TestGorilla',
      integrations: 'ATS integrations and API access on Plus',
      pricing: 'Free $0; Core $142/month ($1,704/year); Plus from $400/month; Enterprise custom',
      freePlan: 'Free plan: 10 credits a month, 5 essential tests, 1 seat',
    },
    freeNote: 'TestGorilla has a Free plan with 10 credits a month and five essential tests',
    sources: [
      { label: 'TestGorilla pricing', url: 'https://www.testgorilla.com/pricing/' },
      { label: 'TestGorilla one-way AI video interviews', url: 'https://support.testgorilla.com/hc/en-us/articles/45610232362395-One-way-AI-video-interviews' },
      { label: 'TestGorilla ATS or API connections', url: 'https://support.testgorilla.com/hc/en-us/articles/43190734513691-Connect-your-ATS-or-API' },
    ],
  },
  willo: {
    id: 'willo',
    name: 'Willo',
    website: 'https://www.willo.video',
    category: 'Async video, audio and written interviews',
    bestFor: 'High-volume and multilingual hiring that needs video, audio or written async responses',
    summary: 'Willo is an asynchronous interviewing platform where candidates answer with video, audio, written responses, file uploads or multiple choice in 18+ languages. Willo Intelligence adds transcription, summaries, benchmarking and candidate ranking.',
    interviewFormats: 'Async video, audio, written, file upload, multiple choice',
    aiSummary: 'Willo Intelligence: transcription, summaries, benchmarking, ranking',
    startingPrice: '£49 per live role/mo',
    strengths: [
      'Several response formats beyond video',
      'Candidate invitations by email and SMS in 18+ languages',
      'Custom branding, anti-cheat and WCAG-compliant candidate experience',
      'Enterprise integrations with 20+ ATS platforms, including Workday and Bullhorn',
    ],
    watchOuts: [
      'Lite is capped at five live roles, five users and 150 assessed candidates per role',
      'Lite integrations are limited to Zapier and Integrately',
      'Application screening before the interview is not listed as a core feature',
    ],
    facts: {
      focus: 'Asynchronous interviews in several response formats for high-volume hiring',
      resume: 'Not listed as a core feature; confirm with Willo',
      video: 'Async video, audio, written, file upload and multiple-choice responses',
      ai: 'Willo Intelligence: transcription, summaries, benchmarking and ranking',
      assessments: 'Assessment library with custom scorecards',
      clientSharing: 'Confirm external client-sharing workflow with Willo',
      integrations: 'Lite: Zapier and Integrately; Enterprise: 20+ ATS platforms and open API',
      pricing: 'Lite £49 per live role per month (up to 5 roles); Enterprise from £2,999/year',
      freePlan: 'No free plan listed',
    },
    sources: [
      { label: 'Willo pricing', url: 'https://www.willo.video/pricing' },
      { label: 'Willo platform', url: 'https://www.willo.video/' },
    ],
  },
  vervoe: {
    id: 'vervoe',
    name: 'Vervoe',
    website: 'https://vervoe.com',
    category: 'Skills assessments and job simulations',
    bestFor: 'Skills-first hiring where job simulations are the main evidence of ability',
    summary: 'Vervoe tests candidates with job simulations such as coding challenges, spreadsheet tasks, presentations, written and video responses, then uses AI grading to produce ranked shortlists. It also offers conversational AI screening.',
    interviewFormats: 'Video as one assessment question type',
    aiSummary: 'AI grading, AI-ranked shortlists, conversational AI screening',
    startingPrice: 'Not published (request)',
    strengths: [
      'Role-specific job simulations rather than generic tests',
      'AI grading and AI-ranked shortlists',
      'Many question types: coding, spreadsheets, presentations, video',
      'Integrates with major ATS platforms and offers an API',
    ],
    watchOuts: [
      'Pricing is available on request only',
      'Built around assessments; a structured interview step may need another tool',
      'Longer job simulations ask more of candidates than a short screening step',
    ],
    facts: {
      focus: 'Skills assessments and job simulations with AI grading',
      resume: 'Positions skills evidence over resumes; confirm resume screening with Vervoe',
      video: 'Video responses as one assessment question type',
      ai: 'AI grading, AI-ranked shortlists and conversational AI screening',
      assessments: 'Role-specific skills tests, coding, spreadsheet and presentation tasks',
      clientSharing: 'Confirm with Vervoe',
      integrations: 'Major ATS platforms and custom integrations via API',
      pricing: 'Not published; request pricing',
      freePlan: 'No free plan listed',
    },
    sources: [
      { label: 'Vervoe pricing', url: 'https://vervoe.com/pricing/' },
      { label: 'Vervoe platform', url: 'https://vervoe.com/' },
    ],
  },
  hireflix: {
    id: 'hireflix',
    name: 'Hireflix',
    website: 'https://www.hireflix.com',
    category: 'Simple one-way video interviews',
    bestFor: 'Teams that want simple one-way video interviews with unlimited seats at a published price',
    summary: 'Hireflix is a one-way video interview tool where candidates record answers without an app or registration. Every plan includes unlimited seats, positions and responses, custom branding and automatic transcription.',
    interviewFormats: 'One-way video',
    aiSummary: 'Automatic transcription; AI scoring not listed',
    startingPrice: '$75/mo billed yearly',
    strengths: [
      'Unlimited users, positions and interview responses on every plan',
      'No app download or registration for candidates',
      'Email, SMS and WhatsApp candidate communications',
      'Native ATS integrations plus Zapier, Make and a GraphQL API with webhooks',
    ],
    watchOuts: [
      'AI scoring of answers is not listed',
      'No resume screening or assessments in the same tool',
      'Plans are priced by company size, so growing teams change tiers',
    ],
    facts: {
      focus: 'One-way video interviews only',
      resume: 'Not listed',
      video: 'One-way video interviews with no candidate app or registration',
      ai: 'Automatic video transcription; AI scoring not listed',
      assessments: 'Not listed',
      clientSharing: 'Team review and sharing; Custom plan lists recruitment agencies',
      integrations: 'Lever, BambooHR, Greenhouse, Workable, Ashby; Zapier, Make; GraphQL API with webhooks',
      pricing: 'Small $75/month and Medium $150/month billed yearly; Custom for agencies and high volume',
      freePlan: 'Free first month on all plans',
    },
    freeNote: 'Hireflix offers a free first month on its plans',
    sources: [
      { label: 'Hireflix pricing', url: 'https://www.hireflix.com/pricing' },
      { label: 'Hireflix platform', url: 'https://www.hireflix.com/' },
    ],
  },
  vidcruiter: {
    id: 'vidcruiter',
    name: 'VidCruiter',
    website: 'https://vidcruiter.com',
    category: 'Structured hiring suite',
    bestFor: 'Large and regulated employers (public sector, healthcare, education) that need a full structured-hiring suite',
    summary: 'VidCruiter is a structured hiring suite with pre-recorded and live video interviews, self-scheduling, structured interview guides, automated references, skills testing, video proctoring and an ATS. Its AI scores candidates against rubrics, writes notes and summaries, and flags potential interview fraud.',
    interviewFormats: 'Pre-recorded and live video',
    aiSummary: 'Rubric scoring, notes and summaries, interview guides, fraud detection',
    startingPrice: 'Not published (request)',
    strengths: [
      'Pre-recorded and live interviews with rating guides',
      'Self-scheduling, automated references, skills testing and proctoring',
      'SOC 2 Type II certified, with GDPR and PIPEDA compliant data handling',
      'AI identity verification and deepfake detection',
    ],
    watchOuts: [
      'Pricing is available through a request form only',
      'Suite scope may exceed what a team needs for video screening alone',
      'Confirm which modules are priced separately',
    ],
    facts: {
      focus: 'Structured hiring suite: live and pre-recorded video, scheduling, references, testing',
      resume: 'AI candidate screening against role-specific rubrics',
      video: 'Pre-recorded and live video interviews with rating guides',
      ai: 'Rubric scoring, notes, transcripts, summaries, interview guides and fraud detection',
      assessments: 'Skills testing and video proctoring',
      clientSharing: 'Confirm with VidCruiter',
      integrations: 'Own ATS plus ATS integrations; confirm your system',
      pricing: 'Not published; pricing via request form',
      freePlan: 'No free plan listed',
    },
    sources: [
      { label: 'VidCruiter pricing', url: 'https://vidcruiter.com/pricing/' },
      { label: 'VidCruiter platform', url: 'https://vidcruiter.com/' },
    ],
  },
  alex: {
    id: 'alex',
    name: 'Alex',
    website: 'https://www.alex.com',
    category: 'AI recruiter for staffing and RPO',
    bestFor: 'Staffing and RPO firms that want an AI recruiter to screen every applicant and interview by video, phone or chat, under each client’s brand',
    summary: 'Alex is an AI recruiter that screens resumes, resurfaces past candidates in the ATS, runs two-way AI interviews by video, phone, SMS or WhatsApp, checks identity and schedules next steps before handing qualified candidates to a recruiter. It can present as each of an agency’s clients, with their own scripts and languages.',
    interviewFormats: 'Two-way AI interviews by video, phone, SMS and WhatsApp',
    aiSummary: 'AI interviewer, resume scoring, role-fit scores, identity and fraud checks',
    startingPrice: 'Not published (demo)',
    strengths: [
      'Interviews by video, phone or chat from one AI recruiter',
      'Per-client scripts, branding and languages for staffing and RPO',
      'Names 33+ officially supported ATS integrations, including Bullhorn',
      'Identity, fraud and knowledge checks during interviews',
    ],
    watchOuts: [
      'Pricing is not published; the pricing page leads to a demo booking',
      'Its pages give 26 to 30 supported languages; confirm yours',
      'The operating company is Apriora Inc., trading as Alex',
    ],
    facts: {
      focus: 'AI recruiter: resume screening, AI interviews, verification and scheduling',
      resume: 'Scores every resume and resurfaces past candidates in the ATS',
      video: 'Two-way AI interviews by video, plus phone, SMS and WhatsApp',
      ai: 'AI interviewer, role-fit scores, searchable transcripts and an assistant to ask about candidates',
      assessments: 'Knowledge checks and proctoring during interviews',
      clientSharing: 'Per-client scripts and branding; client sharing not stated',
      integrations: '33+ officially supported ATS integrations, including Bullhorn, Greenhouse and Loxo',
      pricing: 'Not published; demo and quote',
      freePlan: 'Not stated',
    },
    sources: [
      { label: 'Alex AI Interviewer', url: 'https://www.alex.com/product/ai-interviewer' },
      { label: 'Alex for RPO and staffing', url: 'https://www.alex.com/industries/recruitment-process-outsourcing' },
      { label: 'Alex integrations', url: 'https://www.alex.com/integrations' },
    ],
    reviewed: '2026-09-30',
  },
  heymilo: {
    id: 'heymilo',
    name: 'HeyMilo',
    website: 'https://www.heymilo.ai',
    category: 'AI voice and video screening',
    bestFor: 'High-volume staffing desks that want every applicant screened by AI phone or video interview, with results written back to Bullhorn',
    summary: 'HeyMilo screens and interviews every applicant through AI phone and two-way video interviews, SMS and forms, and sends transcript-backed scores and reports back to the ATS. Its Bullhorn integration writes reports to notes and creates job submissions.',
    interviewFormats: 'AI phone and two-way video interviews; SMS and forms',
    aiSummary: 'Resume scoring, AI phone and video interviews, cheating detection',
    startingPrice: 'Not published (demo)',
    strengths: [
      'AI phone interviews and scored two-way video interviews',
      'Two-way sync with 20+ ATS, including Bullhorn and Recruit CRM',
      'Cheating detection, also sold on its own as HeyMilo Red',
      'White labelling, a public API and an MCP server',
    ],
    watchOuts: [
      'Pricing is not published; the pricing page leads to a demo booking',
      'Language counts differ by page: 20+ overall, 14+ for phone interviews',
      'JobAdder and Vincere are not named among its integrations',
    ],
    facts: {
      focus: 'AI recruiter for high-volume screening by voice, video, SMS and forms',
      resume: 'Resume auto-scoring and shortlisting',
      video: 'Scored two-way AI video interviews and AI phone interviews',
      ai: 'Transcript-backed scores and reports; cheating detection',
      assessments: 'Scenario-based skill assessments',
      clientSharing: 'Staffing solution with white labelling; client sharing not stated',
      integrations: '20+ ATS with two-way sync, including Bullhorn; public API and MCP server',
      pricing: 'Not published; demo and quote',
      freePlan: 'Not stated',
    },
    sources: [
      { label: 'HeyMilo candidate interviewing', url: 'https://www.heymilo.ai/interviewing-candidates' },
      { label: 'HeyMilo for staffing agencies', url: 'https://www.heymilo.ai/solutions/staffing-agencies' },
      { label: 'HeyMilo integrations', url: 'https://www.heymilo.ai/integrations' },
    ],
    reviewed: '2026-09-30',
  },
  tenzo: {
    id: 'tenzo',
    name: 'Tenzo',
    website: 'https://www.tenzo.ai',
    category: 'AI voice recruiter',
    bestFor: 'Staffing agencies and RPOs that want every applicant contacted and interviewed by an AI recruiter within minutes, on top of an existing ATS',
    summary: 'Tenzo is an AI recruiter that sits on top of the ATS you already use. Its interviewer, Morgan, contacts applicants by phone, text, email or WhatsApp, interviews them against a rubric with knockout questions, and writes the score, transcript and recording back to the candidate record.',
    interviewFormats: 'AI voice interviews by phone; text, email and WhatsApp',
    aiSummary: 'Rubric scoring with knockouts, resume review, fraud and identity checks',
    startingPrice: 'Not published (quote)',
    strengths: [
      'Contacts every applicant within minutes by phone, text, email or WhatsApp',
      'Rubric scoring with custom criteria, weighting and knockout questions',
      'Interviews in 40+ languages',
      'Names 57+ ATS and CRM integrations, including Bullhorn',
    ],
    watchOuts: [
      'Pricing is by tailored quote only',
      'Video is listed on the home page but not in its channel lists; confirm it',
      'Not an ATS: it needs one underneath',
    ],
    facts: {
      focus: 'AI recruiter that interviews applicants by voice on top of your ATS',
      resume: 'Resume review',
      video: 'Voice interviews by phone; video listed on the home page',
      ai: 'Rubric scoring with knockout questions; fraud and identity checks',
      assessments: 'Not stated',
      clientSharing: 'Staffing and RPO solution; client sharing not stated',
      integrations: '57+ ATS and CRMs, including Bullhorn and Crelate',
      pricing: 'Not published; tailored quote',
      freePlan: 'Free consultation; trial not stated',
    },
    sources: [
      { label: 'Tenzo solutions', url: 'https://www.tenzo.ai/solutions' },
      { label: 'Tenzo for staffing agencies and RPO', url: 'https://www.tenzo.ai/solutions/staffing-agencies-rpo' },
      { label: 'Tenzo pricing', url: 'https://www.tenzo.ai/pricing' },
    ],
    reviewed: '2026-09-30',
  },
  'glider-ai': {
    id: 'glider-ai',
    name: 'Glider AI',
    website: 'https://glider.ai',
    category: 'AI screening and skills validation',
    bestFor: 'Staffing firms and enterprise hiring teams that want AI phone screens, AI video interviews and proctored skills assessments, with proof of skills for clients',
    summary: 'Glider AI combines AI agents for chat and phone screening, autonomous AI video interviews, one-way video, skills and coding assessments, and proctoring with ID checks. It sells to staffing firms, promising proof of skills to present to clients, and is listed on the Bullhorn Marketplace. In March 2026 Findem announced it was acquiring Glider AI, which will keep its brand.',
    interviewFormats: 'AI phone and AI video interviews; one-way video; chat',
    aiSummary: 'AI phone and video interviews, skills tests, proctoring and ID checks',
    startingPrice: 'Not published (demo)',
    strengths: [
      'AI phone screens and autonomous AI video interviews scored against a rubric',
      'Skills, coding and behavioural assessments with proctoring and ID verification',
      'Integrations with Bullhorn, Ceipal and JobDiva, and VMS platforms such as Beeline and Magnit',
      'Report links that people without a Glider login can open, with personal details masked',
    ],
    watchOuts: [
      'Pricing is not published; the pricing and free-trial pages lead to a demo request',
      'Findem announced in March 2026 that it is acquiring Glider AI',
      'Candidates must share their screen before an AI interview starts',
    ],
    facts: {
      focus: 'Conversational AI for screening, skills validation and interviews',
      resume: 'No resume screening product named; resumes are used to plan AI interviews',
      video: 'Autonomous AI video interviews and one-way video; AI phone screens',
      ai: 'AI agents for chat and phone screening; rubric-scored AI interviews; fraud and ID checks',
      assessments: 'Skills, coding and behavioural assessments with proctoring',
      clientSharing: 'Report links open without a login, with personal details masked; pitched as proof of skills for clients',
      integrations: 'Bullhorn, Ceipal, JobDiva, Greenhouse, Workday, iCIMS and more; VMS: Beeline, Magnit; JobAdder and Vincere not listed',
      pricing: 'Not published; licence or enterprise programme through sales',
      freePlan: 'No free trial; demo request',
    },
    sources: [
      { label: 'Glider AI Recruiter', url: 'https://glider.ai/product/ai-recruiter/' },
      { label: 'Glider AI video interviews', url: 'https://glider.ai/product/ai-interview-software/ai-video-interviews/' },
      { label: 'Glider AI for staffing firms', url: 'https://offers.glider.ai/solutions/staffing-firms' },
      { label: 'Glider AI integrations', url: 'https://offers.glider.ai/resources/integrations-partnerships/' },
      { label: 'Glider AI help: sharing reports', url: 'https://glider.freshdesk.com/support/solutions/articles/70000419562-share-report-with-the-team-' },
      { label: 'Findem to acquire Glider AI', url: 'https://www.findem.ai/news/findem-to-acquire-glider-ai-to-deliver-hire-ready-candidates' },
    ],
    reviewed: '2026-10-01',
  },
  paradox: {
    id: 'paradox',
    name: 'Paradox',
    website: 'https://www.paradox.ai',
    category: 'Conversational AI for high-volume hiring',
    bestFor: 'Employers hiring high volumes of frontline and hourly staff who want screening, scheduling and candidate questions handled by chat',
    summary: 'Paradox’s AI assistant, Olivia, screens applicants against role requirements by chat or text, schedules and reschedules interviews, answers candidate questions in more than 100 languages, and handles offers and onboarding. Paradox also sells a conversational ATS. Its site now carries “A Workday Company”.',
    interviewFormats: 'Chat and text screening; recorded video responses; live in-browser video',
    aiSummary: 'Chat screening, self-scheduling, candidate Q&A in 100+ languages',
    startingPrice: 'Not published (demo)',
    strengths: [
      'Screening, scheduling and candidate questions by SMS, WhatsApp and web chat',
      'Interview self-scheduling, including panel interviews',
      'Offer letters and onboarding paperwork in the same conversation',
      'Integrations with Workday and SAP SuccessFactors, plus an open API',
    ],
    watchOuts: [
      'Pricing is not published; the pricing page leads to a demo request',
      'Industry pages cover employers only; there is no staffing solution',
      'No AI voice or phone interviews are described',
    ],
    facts: {
      focus: 'Conversational AI assistant for high-volume and frontline hiring',
      resume: 'Screens applicants against role requirements by chat or text',
      video: 'Recorded video responses and live in-browser video interviews',
      ai: 'Chat screening, self-scheduling and candidate Q&A in 100+ languages',
      assessments: 'Not stated',
      clientSharing: 'Not stated; built for employers',
      integrations: 'Workday, SAP SuccessFactors, Indeed and an open API; Bullhorn not named',
      pricing: 'Not published; demo request',
      freePlan: 'Not stated',
    },
    sources: [
      { label: 'Paradox home page', url: 'https://www.paradox.ai/' },
      { label: 'Paradox screening', url: 'https://www.paradox.ai/products/screening' },
      { label: 'Paradox integrations', url: 'https://www.paradox.ai/partners/integrations' },
      { label: 'Paradox FAQs', url: 'https://www.paradox.ai/faqs' },
    ],
    reviewed: '2026-09-30',
  },
  sapia: {
    id: 'sapia',
    name: 'Sapia.ai',
    website: 'https://sapia.ai',
    category: 'AI chat interviews for enterprise volume hiring',
    bestFor: 'Employers and RPOs hiring more than 500 people a year who want every applicant to take a structured, text-based AI interview',
    summary: 'Sapia.ai gives every applicant a structured, text-only AI chat interview, then ranks candidates with explainable scores and sends each one personalised insights. Top applicants can move on to a one-way video interview that the hiring team rates; the AI does not score it. Sapia.ai prices per hire, with unlimited interviews, and says it is designed for companies that hire more than 500 people a year.',
    interviewFormats: 'Text-based AI chat interview; one-way video rated by people',
    aiSummary: 'Chat interview scoring, explainable rankings, candidate insights',
    startingPrice: 'Per hire (quote)',
    strengths: [
      'A structured chat interview for every applicant, with personalised insights for each one',
      'Explainable scores, each traced back to the answers behind it',
      'Priced per hire, with unlimited interviews',
      'Integrations with Workday, SAP SuccessFactors, SmartRecruiters, iCIMS and Greenhouse',
    ],
    watchOuts: [
      'Designed for companies hiring more than 500 people a year',
      'Its AI scores written answers only; the video interview is rated by people',
      'It does not screen CVs, and Bullhorn, JobAdder and Vincere are not named',
    ],
    facts: {
      focus: 'Text-based AI chat interviews for enterprise volume hiring',
      resume: 'No CV screening; the chat interview replaces the CV',
      video: 'One-way video for top applicants, rated by the hiring team, not scored by AI',
      ai: 'Structured chat interview scoring with explainable profiles and candidate insights',
      assessments: 'The chat interview replaces a separate assessment',
      clientSharing: 'No client share link stated; its RPO page builds scoring models for each client’s roles',
      integrations: 'Workday, SAP SuccessFactors, SmartRecruiters, iCIMS, Greenhouse, Teamtailor and more; Bullhorn not named',
      pricing: 'Per hire, tailored rate; not published; custom integrations may add fixed costs',
      freePlan: 'Not stated',
    },
    sources: [
      { label: 'Sapia.ai chat interview', url: 'https://sapia.ai/platform/interview/' },
      { label: 'Sapia.ai video interview', url: 'https://sapia.ai/products/video-interview/' },
      { label: 'Sapia.ai for RPOs', url: 'https://sapia.ai/solutions/industry/rpo-staffing/' },
      { label: 'Sapia.ai integrations', url: 'https://sapia.ai/products/integrations/' },
      { label: 'Sapia.ai pricing', url: 'https://sapia.ai/pricing/' },
    ],
    reviewed: '2026-10-01',
  },
  harver: {
    id: 'harver',
    name: 'Harver',
    website: 'https://harver.com',
    category: 'Assessments and video interviews for volume hiring',
    bestFor: 'Enterprises hiring at volume for contact centre, retail, frontline and hourly roles that want assessments, video interviews, scheduling and reference checks from one vendor',
    summary: 'Harver sells pre-employment assessments, including pymetrics game-based assessments, plus one-way and live video interviews, interview scheduling and Checkster reference checks. It says it helps BPOs, contact centres and retail organisations hire at volume. Recruiters rate Harver’s video interview answers; the AI does not. In September 2026 Harver announced it had acquired Symphony Talent, a recruitment marketing company. Pricing is not published.',
    interviewFormats: 'One-way video; live interviews with automated scheduling',
    aiSummary: 'Assessment scoring and job matching, fraud detection; video answers rated by people',
    startingPrice: 'Not published (demo)',
    strengths: [
      'Over 450 assessment options and 900+ pre-built job profiles',
      'pymetrics game-based assessments and video-based situational judgement tests',
      'Video interviews, scheduling and Checkster reference checks in one platform',
      'Candidates can be shared with external hiring managers, who can rate video interviews',
    ],
    watchOuts: [
      'Pricing is not published; there is no pricing page, only a demo request',
      'Video interview answers are rated by recruiters, not by AI',
      'Built for enterprise volume hiring; Bullhorn, JobAdder and Vincere are not listed',
    ],
    facts: {
      focus: 'Assessments and video interviews for enterprise volume hiring',
      resume: 'No resume screening stated; candidates are screened with job-relevant questions and tests',
      video: 'One-way video rated by people; live interview option with automated scheduling',
      ai: 'Assessment scoring and job matching, fraud detection, automated writing and spoken-language scoring; AI supports decisions',
      assessments: 'Over 450 options: behavioural, pymetrics games, cognitive, situational judgement, skills and language',
      clientSharing: 'External users can be given guest access to a candidate, expiring within a month',
      integrations: 'Workday, iCIMS, UKG, Greenhouse, Lever and more; Bullhorn, JobAdder and Vincere not listed; API for customers',
      pricing: 'Not published; demo and proposal',
      freePlan: 'Not stated',
    },
    sources: [
      { label: 'Harver products', url: 'https://harver.com/software/' },
      { label: 'Harver video interview', url: 'https://harver.com/software/interview/' },
      { label: 'Harver assessments', url: 'https://harver.com/software/assessments/' },
      { label: 'Harver integrations', url: 'https://harver.com/integrations/' },
      { label: 'Harver help: rating video interviews', url: 'https://knowledge.harver.com/hc/en-us/articles/41004109799441-Rating-Virtual-Interviews' },
      { label: 'Harver help: sharing candidates', url: 'https://knowledge.harver.com/hc/en-us/articles/13329083292561-Sharing-Candidate-Detail-Page' },
      { label: 'Harver acquires Symphony Talent', url: 'https://harver.com/press/harver-acquires-symphony-talent/' },
    ],
    reviewed: '2026-10-01',
  },
  'criteria-corp': {
    id: 'criteria-corp',
    name: 'Criteria',
    website: 'https://www.criteriacorp.com',
    category: 'Pre-employment assessments and video interviews',
    bestFor: 'Employers that want validated cognitive, personality and skills tests, with one-way and live video interviews, on a quoted subscription',
    summary: 'Criteria’s Talent Signal platform combines cognitive aptitude tests such as the CCAT with personality, emotional intelligence, risk, skills and game-based tests, plus timed one-way and live video interviews. An AI Scoring add-on rates one-way interview answers from their transcripts, and an AI Interview Agent is marked coming soon. Pricing is a quoted subscription built around headcount.',
    interviewFormats: 'Timed one-way video; live video in the browser',
    aiSummary: 'AI scoring of one-way interview transcripts (add-on), AI proctoring, interview summaries',
    startingPrice: 'Not published (quote)',
    strengths: [
      'Cognitive, personality, emotional intelligence, risk, skills and game-based tests',
      'One-way and live video interviews alongside the tests',
      'AI interview scores based on transcripts, not appearance or tone of voice',
      'JobAdder and iCIMS integrations included in every subscription',
    ],
    watchOuts: [
      'Its terms require recruitment agencies to declare themselves and get written consent, with separate agency pricing',
      'AI Scoring is a paid add-on, and the AI Interview Agent is marked coming soon',
      'The Bullhorn integration is a paid add-on with a 4–8 week setup',
    ],
    facts: {
      focus: 'Pre-employment assessments plus one-way and live video interviews',
      resume: 'No resume screening product stated',
      video: 'Timed one-way video and live browser video interviews',
      ai: 'AI Scoring add-on rates one-way answers from transcripts; AI proctoring; AI Interview Agent coming soon',
      assessments: 'Cognitive (including the CCAT), personality, emotional intelligence, risk, skills and game-based tests',
      clientSharing: 'Not stated; interviews are reviewed by the hiring team, and agency use needs written consent',
      integrations: 'JobAdder and iCIMS included; Bullhorn and Workday as paid add-ons; Greenhouse and Lever; Vincere not listed',
      pricing: 'Not published; quoted subscription built around headcount, with paid add-ons',
      freePlan: '14-day free trial for employers',
    },
    freeNote: 'Criteria offers a 14-day free trial, which its sign-up page limits to employers',
    sources: [
      { label: 'Criteria plans and pricing', url: 'https://www.criteriacorp.com/plans-and-pricing' },
      { label: 'Criteria assessments', url: 'https://www.criteriacorp.com/assess' },
      { label: 'Criteria interviewing', url: 'https://www.criteriacorp.com/interview' },
      { label: 'Criteria AI Scoring', url: 'https://www.criteriacorp.com/interview/AI-Scoring' },
      { label: 'Criteria Bullhorn integration', url: 'https://www.criteriacorp.com/integrations-and-partners/bullhorn' },
      { label: 'Criteria terms of use', url: 'https://www.criteriacorp.com/terms-use' },
      { label: 'Criteria free trial', url: 'https://www.criteriacorp.com/free-trial' },
    ],
    reviewed: '2026-10-01',
  },
  metaview: {
    id: 'metaview',
    name: 'Metaview',
    website: 'https://www.metaview.ai',
    category: 'AI interview notes and recruiting agents',
    bestFor: 'Agencies, executive search firms and in-house teams that want every live interview written up automatically, with optional sourcing, application review and AI voice screening agents',
    summary: 'Metaview’s AI notetaker records interviews on Zoom, Google Meet, Teams and Webex, by phone or in person, with or without a bot, and writes notes and scorecards into the ATS. Separately priced agents add sourcing, application review, async AI voice screening and email outreach. It names recruiting agencies and executive search partners among its users.',
    interviewFormats: 'Notes on live video, phone and in-person interviews; async AI voice screens',
    aiSummary: 'Interview notes and scorecards, application review, AI voice screening, sourcing',
    startingPrice: 'Free; Notetaker Pro $60/user/month',
    strengths: [
      'Notes from video, phone and in-person interviews, with or without a bot',
      'Published prices, with free tiers for notes, sourcing and application review',
      'Integrates with Bullhorn, JobAdder, Loxo and Recruit CRM',
      'Notes and recordings can be shared by link with people who have no account',
    ],
    watchOuts: [
      'The AI Screening agent runs on credits whose price is not published',
      'The free Notetaker plan keeps only the last 14 days of calls',
      'Vincere is not listed, and supported ATS differ by product',
    ],
    facts: {
      focus: 'AI interview notetaker, plus sourcing, application review and AI voice screening agents',
      resume: 'Application Review rates applicants against an AI-built profile; free for 50 a month',
      video: 'Records live video, phone and in-person interviews; async AI screens are voice-led, with optional candidate video',
      ai: 'AI notes, scorecard autofill, applicant ratings and rubric-scored AI voice screens with fraud flags',
      assessments: 'No test library; AI screens can include role-plays and code questions',
      clientSharing: 'Notes, transcripts and recordings can be shared by link without an account',
      integrations: '113 ATS listed, including Bullhorn, JobAdder, Loxo and Recruit CRM (not Vincere); MCP server and Zapier',
      pricing: 'Notetaker Free or Pro $60/user/month; Sourcing $100 or $300/user/month; Application Review from $150/month; Screening credits not published',
      freePlan: 'Free plans: 25 calls per user a month with 14 days of history; 50 application reviews a month',
    },
    freeNote: 'Metaview has free plans, including 25 recorded calls per user a month with 14 days of history and 50 application reviews a month',
    sources: [
      { label: 'Metaview pricing', url: 'https://www.metaview.ai/pricing' },
      { label: 'Metaview interview notes', url: 'https://www.metaview.ai/interview-notes' },
      { label: 'Metaview Screening', url: 'https://www.metaview.ai/screening' },
      { label: 'Metaview integrations', url: 'https://www.metaview.ai/integrations' },
      { label: 'Metaview help: subscriptions', url: 'https://support.metaview.ai/account-management/managing-subscriptions' },
    ],
    reviewed: '2026-10-01',
  },
  brighthire: {
    id: 'brighthire',
    name: 'BrightHire',
    website: 'https://brighthire.com',
    category: 'Interview intelligence and AI interviewer',
    bestFor: 'In-house talent teams, especially enterprises, that want structured live interviews, AI notes and scorecards, and an async AI first-round screen',
    summary: 'BrightHire records and transcribes live Zoom, Google Meet, Teams, phone and in-person interviews, writes AI notes, fills ATS scorecards in one click and adds interview planning, coaching and analytics. BrightHire Screen adds an async AI interviewer by voice or video, scored against your rubrics. BrightHire says it is now a Zoom company and continues as a standalone brand.',
    interviewFormats: 'Notes on live video, phone and in-person interviews; async AI voice or video screens',
    aiSummary: 'AI notes, one-click scorecards, coaching, rubric-scored AI screens, fraud signals',
    startingPrice: 'Not published (demo)',
    strengths: [
      'Recorded live interviews and an async AI interviewer that share the same rubrics',
      'One-click ATS scorecards, with highlights playable inside the ATS',
      'Candidate fraud detection across AI screens and live interviews',
      'SOC 2 Type II, an annual independent bias audit and region-based consent',
    ],
    watchOuts: [
      'No prices are published; every plan starts with a demo',
      'Its AI screen runs in English, Latin American Spanish and Brazilian Portuguese only',
      'No agency or staffing positioning; JobAdder, Vincere, Loxo and Recruit CRM are not listed',
    ],
    facts: {
      focus: 'Interview intelligence for in-house teams, plus the BrightHire Screen AI interviewer',
      resume: 'No resume screening product stated',
      video: 'Records live Zoom, Meet and Teams, phone and in-person interviews; async AI screens by voice or video',
      ai: 'AI notes, one-click scorecards, coaching and analytics; rubric-scored AI screens with fraud signals',
      assessments: 'No test library stated; screens are scored against your rubrics',
      clientSharing: 'Sharing, clips and playlists on the Teams tier; sharing outside the company not stated',
      integrations: '18 ATS listed, including Bullhorn and Greenhouse; its AI screen lists Greenhouse, Ashby, Workday and SmartRecruiters; MCP and Zapier',
      pricing: 'Not published; the AI screen is priced by interview volume',
      freePlan: 'No free plan; Greenhouse customers can get 3 months free for 5 users',
    },
    sources: [
      { label: 'BrightHire pricing', url: 'https://brighthire.com/pricing/' },
      { label: 'BrightHire Screen', url: 'https://brighthire.com/ai-interviewer/' },
      { label: 'BrightHire integrations', url: 'https://brighthire.com/product/integrations/' },
      { label: 'BrightHire joins Zoom', url: 'https://brighthire.com/blog/brighthire-joins-zoom/' },
      { label: 'BrightHire Screen languages', url: 'https://help.brighthire.ai/en/articles/16293176-supported-languages-for-brighthire-screen' },
    ],
    reviewed: '2026-10-01',
  },
  talview: {
    id: 'talview',
    name: 'Talview',
    website: 'https://www.talview.com',
    category: 'AI interviews, proctoring and identity checks',
    bestFor: 'Large employers and exam bodies running high-volume or high-stakes hiring and testing that need identity verification and proctoring',
    summary: 'Talview is an enterprise platform for interviewing and exam proctoring. For hiring it offers one-way video interviews, live interview rooms, an AI interviewer called Ivy, recorded phone screening, AI resume scoring and assessments, with identity and fraud checks throughout. It does not publish prices; quotes are unit-based with a minimum commitment.',
    interviewFormats: 'One-way and live video; Ivy AI interviewer; recorded phone screens',
    aiSummary: 'AI interviewer, resume fitment scores, interview summaries, fraud and deepfake checks',
    startingPrice: 'Not published (unit-based quote)',
    strengths: [
      'One-way video, live interview rooms, an AI interviewer and phone screening on one platform',
      'Identity and fraud controls, including face and voice matching and deepfake scans',
      '2,000+ assessments across 500+ skills, plus coding tests in 80+ languages',
      'REST and Graph APIs with webhooks',
    ],
    watchOuts: [
      'No published prices; its pricing FAQ cites a typical minimum of about 150 to 200 candidates',
      'No staffing solution, and Bullhorn, JobAdder, Vincere, Loxo and Recruit CRM are not listed',
      'No free plan; its “Try for Free” button leads to a demo request',
    ],
    facts: {
      focus: 'Enterprise AI interviewing and exam proctoring with identity verification',
      resume: 'AI resume fitment: Strong, Potential or Low Fit, with a written explanation',
      video: 'One-way video, live interview rooms and the Ivy AI interviewer',
      ai: 'AI interviewer, resume fitment, interview summaries and scorecards, fraud and deepfake detection',
      assessments: '2,000+ assessments across 500+ skills; coding tests; built-in proctoring',
      clientSharing: 'No client portal stated; guest interviewers allowed from approved client domains',
      integrations: 'Workday, SuccessFactors, Greenhouse, iCIMS and more; agency ATS not listed; REST and Graph APIs',
      pricing: 'Not published; unit-based quote, with a typical minimum of about 150 to 200 candidates',
      freePlan: 'No free plan; a product walkthrough or a pilot',
    },
    sources: [
      { label: 'Talview pricing and FAQ', url: 'https://www.talview.com/en/pricing' },
      { label: 'Talview Ivy AI Interviewer', url: 'https://www.talview.com/en/ivy-ai-interviewer' },
      { label: 'Talview on-demand video interviews', url: 'https://www.talview.com/en/on-demand-video-interviewing' },
      { label: 'Talview integrations', url: 'https://www.talview.com/en/integrations' },
      { label: 'Talview Trust Center', url: 'https://www.talview.com/trust-center' },
    ],
    reviewed: '2026-10-01',
  },
  'interviewer-ai': {
    id: 'interviewer-ai',
    name: 'Interviewer.AI',
    website: 'https://interviewer.ai',
    category: 'AI one-way video and avatar interviews',
    bestFor: 'Hiring teams and staffing agencies screening high volumes of applicants with self-serve AI video interviews',
    summary: 'Interviewer.AI scores resumes, runs one-way video interviews and, on higher plans, conversational interviews with an AI avatar, then ranks candidates with score breakdowns and fraud flags. It has a staffing agency page, lets recruiters share shortlists with clients by email link, and publishes self-serve plans from $199 a month with a 14-day trial.',
    interviewFormats: 'One-way video; conversational AI avatar interviews (Professional and above)',
    aiSummary: 'Resume scoring, answer relevancy scores, AI summaries, fraud flags',
    startingPrice: '$166/mo billed annually ($199 monthly)',
    strengths: [
      'Published self-serve plans, with interview credits that roll over',
      'A staffing agency page and client sharing by email link, with no client login',
      'Lists Bullhorn, JobAdder, Vincere and Loxo among its ATS integrations',
      'Resume scoring, one-way video and AI avatar interviews in one workflow',
    ],
    watchOuts: [
      'Native ATS integrations need annual Professional with a one-time fee, or Enterprise',
      'Essential allows 3 job posts and 50 credits a month; avatar interviews use 2 credits and need Professional',
      'No live video interviews on the platform, and no refunds',
    ],
    facts: {
      focus: 'AI one-way video and avatar interviews for high-volume screening',
      resume: 'AI resume scoring and profile summary on all plans',
      video: 'One-way video on all plans; AI avatar interviews on Professional and above; no live video',
      ai: 'Resume scoring, answer relevancy scores, AI summaries and fraud flags',
      assessments: 'WorkMap competency and behavioural assessment on all plans',
      clientSharing: 'Share shortlists, videos and scores with clients by email link; no client login needed',
      integrations: 'Bullhorn, JobAdder, Vincere, Loxo, Greenhouse, Workday and more; Zapier; API on Enterprise',
      pricing: 'Essential $199/month or $1,990/year; Professional $399/month or $3,990/year; Enterprise custom',
      freePlan: '14-day trial with up to 5 AI interview assessments',
    },
    freeNote: 'Interviewer.AI offers a 14-day free trial with up to 5 AI interview assessments',
    sources: [
      { label: 'Interviewer.AI pricing', url: 'https://interviewer.ai/pricing/' },
      { label: 'Interviewer.AI integrations', url: 'https://interviewer.ai/integrations/' },
      { label: 'Interviewer.AI for staffing agencies', url: 'https://interviewer.ai/staffing-agencies/' },
      { label: 'Help: sharing candidates with clients', url: 'https://help.interviewer.ai/en/articles/11098159-how-do-i-share-candidates-with-my-clients' },
      { label: 'Help: trial period', url: 'https://help.interviewer.ai/en/articles/11098289-how-long-is-the-trial-period' },
    ],
    reviewed: '2026-10-01',
  },
  'employ-interview-companion': {
    id: 'employ-interview-companion',
    name: 'Employ AI Interview Companion',
    website: 'https://www.employinc.com/ai-interview-companion/',
    category: 'Interview intelligence (formerly Pillar)',
    bestFor: 'In-house talent teams, especially on Lever, Jobvite or JazzHR, that want recorded interviews with live interview guides, AI summaries and interviewer coaching',
    summary: 'AI Interview Companion is Pillar’s interview intelligence product, sold by Employ, the company behind Lever, Jobvite and JazzHR, which announced its acquisition of Pillar in March 2025. It records, transcribes and summarises Zoom, Google Meet and Teams interviews and in-platform phone calls, shows interviewers live interview guides, and adds coaching insights and candidate sentiment. Employ says its AI does not score candidates.',
    interviewFormats: 'Notes on live Zoom, Meet and Teams interviews and in-platform phone calls',
    aiSummary: 'Transcripts, summaries, highlights, live interview guides, coaching insights',
    startingPrice: 'Not published (quote)',
    strengths: [
      'Live interview guides and recommended questions during the interview',
      'Phone calls placed in the platform get the same AI summaries and highlights',
      'A consent email with one-click opt-out, a voice prompt and PII redaction',
      'Integrations with 15 ATS, including Workday, Greenhouse, Ashby and Lever',
    ],
    watchOuts: [
      'No published prices; it is sold through Employ’s demo and quote process',
      'Its help centre says the Zoom integration does not work with EU-based Zoom accounts',
      'Bullhorn, JobAdder, Vincere, Loxo and Recruit CRM are not listed',
    ],
    facts: {
      focus: 'Interview intelligence and live interview guidance, sold by ATS maker Employ',
      resume: 'Not part of this product; Employ sells a separate AI Screening Companion',
      video: 'Records live Zoom, Meet and Teams interviews; no one-way video stated',
      ai: 'Transcripts, summaries, highlights, chat over interviews and 17-language translation; no AI candidate scoring',
      assessments: 'Not stated; skills-based interview guides and interviewer ratings',
      clientSharing: 'Highlights can be shared with clients who have platform access; recording links can be made public',
      integrations: '15 ATS, including Workday, Greenhouse, Ashby and Lever; Zoom, Meet and Teams; agency ATS not listed',
      pricing: 'Not published; demo and quote',
      freePlan: 'Not stated',
    },
    sources: [
      { label: 'Employ AI Interview Companion', url: 'https://www.employinc.com/ai-interview-companion/' },
      { label: 'AI Interview Companion integrations', url: 'https://www.employinc.com/ai-interview-companion-integrations/' },
      { label: 'Employ acquires Pillar', url: 'https://www.employinc.com/blog/employ-pillar-supercharged-ai-to-strengthen-human-connection/' },
      { label: 'Employ help: Interview Intelligence FAQs', url: 'https://employsolutionshelp.employinc.com/s/article/Interview-Intelligence-FAQs' },
    ],
    reviewed: '2026-10-01',
  },
  screenloop: {
    id: 'screenloop',
    name: 'Screenloop',
    website: 'https://www.screenloop.com',
    category: 'AI ATS with interview intelligence',
    bestFor: 'In-house talent teams at start-ups and growing companies that want an ATS with a built-in AI interview notetaker, scorecards, references and background checks',
    summary: 'Screenloop is a UK-based AI-native ATS whose interview intelligence records, transcribes and summarises Zoom, Google Meet and Teams interviews and fills in structured scorecards. The notetaker can also run on top of Greenhouse, Lever, Workable, Teamtailor, Ashby or Pinpoint. Screenloop does not publish prices.',
    interviewFormats: 'Notes on live Zoom, Meet and Teams interviews',
    aiSummary: 'AI notetaker, scorecard autofill, clips, interview analytics',
    startingPrice: 'Not published (demo)',
    strengths: [
      'Interview intelligence comes with its ATS at no extra cost, alongside references and background checks',
      'Fills in structured scorecards from the interview',
      'SOC 2 Type 2 audit, a GDPR data processing addendum and automatic candidate anonymisation',
      'Clips can be shared by link with people outside Screenloop',
    ],
    watchOuts: [
      'No published prices; its pricing page redirects to a demo booking',
      'Needs Zoom Pro with cloud recording or Google Workspace; in-person interviews are not recorded',
      'Built for in-house teams; Bullhorn, JobAdder, Vincere and Workday are not listed',
    ],
    facts: {
      focus: 'AI ATS with built-in interview intelligence for in-house teams',
      resume: 'ATS with resume parsing and blind hiring; no AI resume ranking stated',
      video: 'Records live Zoom, Meet and Teams interviews; no one-way video stated',
      ai: 'AI notetaker, summaries, scorecard autofill, clips, an assistant and interview analytics',
      assessments: 'No skills tests stated; reference and background checks',
      clientSharing: 'Clips can be shared by link with non-users (links expire after 30 days by default)',
      integrations: 'Greenhouse, Lever, Workable, Teamtailor, Ashby and Pinpoint; REST API; agency ATS not listed',
      pricing: 'Not published; demo',
      freePlan: 'No standard free plan; its partner referral page offers free access to teams under 30 employees',
    },
    sources: [
      { label: 'Screenloop home page', url: 'https://www.screenloop.com/' },
      { label: 'Screenloop interview intelligence', url: 'https://www.screenloop.com/product/interview-intelligence' },
      { label: 'Screenloop integrations', url: 'https://www.screenloop.com/platform/integrations' },
      { label: 'Screenloop help: AI features', url: 'https://help.screenloop.com/en/articles/16863514-screenloop-ai-features' },
      { label: 'Screenloop help: SOC 2 Type 2', url: 'https://help.screenloop.com/en/articles/6858127-soc-2-type-2-compliant' },
    ],
    reviewed: '2026-10-01',
  },
  myinterview: {
    id: 'myinterview',
    name: 'myInterview',
    website: 'https://www.radancy.com/platform-overview/screening-and-scheduling/',
    category: 'AI screening and one-way video (now Radancy)',
    bestFor: 'Enterprise talent teams on Radancy’s Talent Acquisition Cloud that want AI screening, optional one-way video and automated interview scheduling',
    summary: 'Radancy announced its acquisition of myInterview in September 2025, and myinterview.com now redirects to Radancy’s Screening & Scheduling product. It combines conversational AI screening, optional one-way video answers scored by an LLM against a recruiter’s scorecard, and AI interview scheduling through Outlook, Google Calendar and Teams. Prices are not published, and the old pricing page now returns an error.',
    interviewFormats: 'Optional one-way video; voice, written and multiple-choice answers; conversational AI screening',
    aiSummary: 'LLM scoring against your scorecard, smart shortlisting, AI scheduling',
    startingPrice: 'Not published (Radancy order form)',
    strengths: [
      'A Premium Candidate Sharing add-on, which its help centre recommends for recruitment agencies',
      'Separate branding per job, for teams that hire for several brands or clients',
      'Video, voice, written and multiple-choice answer formats',
      'An AI scheduling agent that syncs Outlook, Google Calendar and Teams',
    ],
    watchOuts: [
      'myinterview.com now redirects to Radancy, and no prices are published',
      'Several features depend on plans or add-ons whose prices and limits are not public',
      'Bullhorn, JobAdder, Vincere, Loxo and Recruit CRM are not named among Radancy’s integrations',
    ],
    facts: {
      focus: 'Radancy’s AI screening and scheduling module (formerly myInterview), for enterprise talent teams',
      resume: 'AI shortlists draw on resume data, applications, chat and video answers',
      video: 'Optional one-way video: up to 10 questions, answers of 30 seconds to 10 minutes; live interviews are scheduled, not hosted',
      ai: 'An LLM scores answers against your scorecard; shortlisting features depend on plan',
      assessments: 'No test library stated; written, single-choice and multiple-choice questions',
      clientSharing: 'Share links that expire after 15 days; a Premium Candidate Sharing add-on collects client feedback',
      integrations: 'Radancy cites 60+ ATS and HCM integrations, including Workday and Greenhouse; agency ATS not named',
      pricing: 'Not published; sold through a Radancy order form',
      freePlan: 'Not stated',
    },
    sources: [
      { label: 'Radancy Screening & Scheduling', url: 'https://www.radancy.com/platform-overview/screening-and-scheduling/' },
      { label: 'Radancy acquires myInterview', url: 'https://www.radancy.com/press-coverage/radancy-extends-ai-platform-powering-faster-smarter-more-cost-efficient-hiring-at-scale/' },
      { label: 'Screening & Scheduling help centre', url: 'https://screeningandscheduling.support.radancy.net/hc/en-us' },
      { label: 'Help: Premium Candidate Sharing', url: 'https://screeningandscheduling.support.radancy.net/hc/en-us/articles/45175550470675-Premium-Candidate-Sharing' },
      { label: 'Radancy integrations', url: 'https://www.radancy.com/partnerships-and-integrations/' },
    ],
    reviewed: '2026-10-01',
  },
  jobma: {
    id: 'jobma',
    name: 'Jobma',
    website: 'https://www.jobma.com',
    category: 'AI video interviews and assessments',
    bestFor: 'Staffing firms and enterprise teams running multi-client video interviewing that want one-way, live and AI-led interviews plus skills tests in one platform',
    summary: 'Jobma is a video interviewing and assessment platform, founded in 2012, with unlimited one-way video interviews on every plan, live and panel interviews, autonomous AI interviews, and skill, coding, game-based and language tests. It has a staffing solution, client access to recordings and summaries, and white-labelling. Its four plans publish detailed limits, but every price is on request.',
    interviewFormats: 'One-way, live and panel video; autonomous AI interviews (Professional and above)',
    aiSummary: 'AI scoring and summaries, resume screening, proctoring, AI-led interviews',
    startingPrice: 'Not published (quote)',
    strengths: [
      'Unlimited one-way video interviews on every plan',
      'Integration pages for Bullhorn, JobAdder, Vincere, Loxo and Recruit CRM',
      'Client access to recordings, summaries and transcripts, with client feedback',
      'Skill, coding, game-based and language tests alongside interviews',
    ],
    watchOuts: [
      'No published prices; all four plans say “Request Pricing”',
      'Starter allows 1 user, 5 active positions and 1 ATS integration, with no AI scoring',
      'AI-led interviews and white-labelling need Professional; Zapier, the API and SSO need Enterprise',
    ],
    facts: {
      focus: 'Video interviewing and skills assessments for enterprise, mid-market and staffing teams',
      resume: 'AI resume screening on Growth and above',
      video: 'Unlimited one-way video on all plans; live video from Growth; panel and autonomous AI interviews on Professional and above',
      ai: 'AI scoring and summaries (Growth and above), AI-led interviews and proctoring; AI is off by default',
      assessments: 'Skill tests on all plans; coding, game-based and AI-built tests from Growth; language tests',
      clientSharing: 'Role-based client access to recordings, summaries and transcripts, with feedback; white-label on Professional and above',
      integrations: 'Bullhorn, JobAdder, Vincere, Loxo, Recruit CRM, Greenhouse and Workday; Zapier and API on Enterprise',
      pricing: 'Not published; four plans, all priced on request',
      freePlan: 'Not stated; demo or quote',
    },
    sources: [
      { label: 'Jobma plans and pricing', url: 'https://www.jobma.com/plans-and-pricing' },
      { label: 'Jobma for staffing and recruitment', url: 'https://www.jobma.com/solutions/staffing-and-recruitment-hiring-software' },
      { label: 'Jobma integrations', url: 'https://www.jobma.com/integrations' },
      { label: 'Jobma FAQs', url: 'https://www.jobma.com/faqs' },
      { label: 'Jobma autonomous AI interviewing', url: 'https://www.jobma.com/autonomous-ai-interviewing' },
    ],
    reviewed: '2026-10-01',
  },
};

export const COMMON_BUYER_QUESTIONS = [
  'How is each AI score produced, and can reviewers see the reasoning?',
  'What notice and consent do candidates receive before a recorded interview?',
  'Which ATS integrations are native, and which plan includes them?',
  'How long are recordings kept, and who can access them?',
];

export const competitors: CompetitorPage[] = [
  {
    id: 'hirevue',
    pricingHref: '/guides/hirevue-pricing',
    name: 'HireVue',
    alternativeIds: ['beatview', 'alex', 'heymilo', 'tenzo', 'glider-ai', 'vidcruiter', 'paradox', 'sapia', 'interviewer-ai', 'sparkhire', 'willo', 'hireflix', 'testgorilla'],
    overview: 'HireVue is an enterprise hiring platform that combines on-demand and live video interviewing, a conversational AI Interviewer, assessments, automated interview scheduling and text-based candidate engagement. HireVue does not publish pricing; plans are quoted through sales.',
    quickAnswer: 'The best HireVue alternative depends on the job HireVue does for you. Recruitment agencies that want resume screening, structured one-way video interviews and password-protected client sharing can use Beatview, which has a free plan. Staffing and RPO firms that want an AI to interview every applicant by phone or video can compare Alex, HeyMilo, Tenzo and Glider AI. Enterprise teams needing live and pre-recorded interviews with scheduling should shortlist VidCruiter; for chat-based volume hiring, Paradox or Sapia.ai. For simpler async video at a published price, compare Spark Hire, Willo or Hireflix; for skills testing, TestGorilla.',
    switchTriggers: [
      { title: 'A price you can budget', body: 'HireVue pricing is quote-based. Teams that need a published price, or a free plan to test on a live role, often compare alternatives.' },
      { title: 'Scope that fits the stage', body: 'If you mainly use one-way video screening, a focused tool can cover that stage without enterprise-suite scope.' },
      { title: 'Agency client delivery', body: 'Recruitment agencies need to share candidates with clients and keep evidence per client role, which enterprise in-house suites are not built around.' },
      { title: 'Visible AI reasoning', body: 'Hiring teams increasingly ask to see why a candidate received a score and what notice candidates get about AI use.' },
    ],
    difference: 'HireVue is an enterprise suite covering video interviews, an AI Interviewer, assessments and scheduling, with quote-based pricing. Beatview is narrower and built for recruitment agencies: it screens applications against configured criteria, runs structured one-way video interviews, scores each answer with visible reasoning and lets you share candidates with clients through password-protected links. Beatview publishes its pricing and has a free plan.',
    chooseBeatview: [
      'You are a recruitment agency presenting shortlists to clients.',
      'You want resume screening and structured one-way interviews in one workflow.',
      'Recruiters need to see the evidence behind every score before deciding.',
      'You want to start free and see published Pro pricing before talking to sales.',
    ],
    keepCompetitor: [
      'You need live video interviews, scheduling and assessments from one enterprise vendor.',
      'HireVue is already integrated with your ATS and HR stack.',
      'You run very high-volume, multi-country hiring with enterprise procurement requirements.',
      'Your assessment program depends on HireVue assessment content.',
    ],
    buyerQuestions: ['Which plan includes the interview formats and AI features we need?', 'What does rollout involve for a team of our size?'],
    ctaHeadline: 'Compare HireVue with Beatview on a live role',
  },
  {
    id: 'sparkhire',
    pricingHref: '/guides/spark-hire-pricing',
    name: 'Spark Hire',
    alternativeIds: ['beatview', 'interviewer-ai', 'hireflix', 'willo', 'vidcruiter', 'hirevue', 'testgorilla'],
    overview: 'Spark Hire is a video interviewing platform for one-way and live interviews with transcription, AI summaries and video scoring. It also sells a behavioral assessment, automated reference checks and an ATS, Spark Hire Recruit, with AI resume review. Video interviews cost $299 per month on a monthly plan or $249 per month billed annually.',
    quickAnswer: 'The best Spark Hire alternative depends on your hiring model. Recruitment agencies that want resume screening, scored one-way interviews and client sharing in one plan can use Beatview, which has a free plan. For low-cost async video, Hireflix and Willo publish entry pricing. For enterprise structured interviewing with scheduling, compare VidCruiter or HireVue; for skills tests, TestGorilla.',
    switchTriggers: [
      { title: 'Job and user limits', body: 'The monthly video plan is limited to five jobs and five users, so teams with changing role counts compare per-role and per-seat pricing.' },
      { title: 'Products priced separately', body: 'Video interviews, behavioral assessments and the ATS are separate products, so costs add up when you need more than one.' },
      { title: 'From recordings to a shortlist', body: 'Teams compare how each tool turns recorded answers into a ranked shortlist with reasons reviewers can check.' },
      { title: 'Agency delivery', body: 'Agencies need client-facing candidate sharing and evidence organised per client role.' },
    ],
    difference: 'Spark Hire offers one-way and live video interviews, with behavioral assessments, reference checks and an ATS sold as separate products. Beatview is a single agency workflow: application screening with a job-match score, structured one-way AI video interviews with per-dimension scoring and ranking, a Work Style assessment and password-protected client sharing. Beatview has a free plan; Pro is $219 per month or $179 per month billed annually.',
    chooseBeatview: [
      'You want screening, interviews and work-style evidence in one plan.',
      'You present candidates to clients and need protected sharing with external notes.',
      'You want to try the workflow free on one live role.',
      'You do not need live video calls inside the platform.',
    ],
    keepCompetitor: [
      'You need live video interviews as well as one-way.',
      'You want automated reference checks from the same vendor.',
      'You plan to use Spark Hire Recruit as your applicant tracking system.',
      'Its 40+ ATS integrations cover your system out of the box.',
    ],
    buyerQuestions: ['What contract length do annual plans require?', 'What counts toward job and user limits on monthly plans?'],
    ctaHeadline: 'See a Spark Hire alternative built for agencies',
  },
  {
    id: 'testgorilla',
    name: 'TestGorilla',
    alternativeIds: ['beatview', 'vervoe', 'criteria-corp', 'harver', 'hirevue', 'willo', 'sparkhire'],
    overview: 'TestGorilla is a pre-employment testing platform with 350+ tests, resume scoring and qualifying questions. Its Plus plan adds AI interviews and video questions with AI scoring, job simulations, custom coding challenges, and ATS and API access. Plans run from Free, to Core at $142 per month, to Plus from $400 per month.',
    quickAnswer: 'The best TestGorilla alternative depends on whether testing is your main need. For skills-first hiring with job simulations, compare Vervoe. For validated cognitive and personality tests such as the CCAT, compare Criteria; for game-based assessments at enterprise volume, Harver. Recruitment agencies that need resume screening, scored one-way video interviews and client sharing in one workflow can use Beatview. For enterprise assessments plus interviewing, consider HireVue; for async video screening, Willo or Spark Hire.',
    switchTriggers: [
      { title: 'Interviews on a higher tier', body: 'AI interviews, video questions with AI scoring, and ATS or API access are listed on Plus, which starts at $400 per month.' },
      { title: 'Credit-based usage', body: 'TestGorilla usage is credit-based and AI interviews use two credits per candidate, so cost moves with volume.' },
      { title: 'Workflow over test library', body: 'Teams that need one connected application-to-interview workflow weigh that against a broad test library.' },
      { title: 'Client review', body: 'Agencies need to confirm how candidates and results are shared with clients.' },
    ],
    difference: 'TestGorilla starts with a large test library and adds AI interviews, video questions and ATS access on its Plus plan. Beatview starts with the agency workflow: branded applications and resume screening, structured one-way AI video interviews with per-dimension scoring, a Work Style assessment and password-protected client sharing. Beatview Pro, which includes AI video interviews, is $219 per month or $179 per month billed annually.',
    chooseBeatview: [
      'You need one application-to-interview workflow for agency delivery.',
      'Recruiters need ranked completed interviews and a candidate event log.',
      'Protected client sharing and external notes are central to your process.',
      'You want AI video interviews without a credit-based plan.',
    ],
    keepCompetitor: [
      'A broad library of pre-employment tests is your main buying requirement.',
      'Your process is organised around skills and cognitive testing before other stages.',
      'Its credits, seats and plan structure fit your hiring volume.',
      'Its supported ATS connection matches your existing system.',
    ],
    buyerQuestions: ['How are credits consumed across tests and interviews?', 'Which plan includes our ATS or API connection?'],
    ctaHeadline: 'See the agency workflow in context',
  },
  {
    id: 'willo',
    name: 'Willo',
    alternativeIds: ['beatview', 'hireflix', 'interviewer-ai', 'sparkhire', 'vidcruiter', 'hirevue'],
    overview: 'Willo is an asynchronous interviewing platform where candidates answer with video, audio, written responses, file uploads or multiple choice, in 18+ languages. Willo Intelligence adds transcription, summaries, benchmarking and candidate ranking. Willo Lite costs £49 per live role per month for up to five roles; Enterprise starts at £2,999 per year.',
    quickAnswer: 'The best Willo alternative depends on what you need beyond async interviews. For an agency workflow with resume screening, scored one-way video interviews and password-protected client sharing, consider Beatview. Hireflix is a simple one-way video option with published pricing and unlimited seats. Spark Hire adds live interviews and an optional ATS; VidCruiter and HireVue suit enterprise structured hiring.',
    switchTriggers: [
      { title: 'Integrations by plan', body: 'Willo Lite lists Zapier and Integrately only; native ATS integrations such as Workday, Greenhouse and Bullhorn are on Enterprise.' },
      { title: 'Per-role limits', body: 'Lite is priced per live role and capped at five roles, five users and 150 assessed candidates per role.' },
      { title: 'Screening before interviews', body: 'Teams that want applications screened before candidates are invited look for that step in the same tool.' },
      { title: 'Client delivery', body: 'Agencies need to share interview evidence with clients without opening their workspace.' },
    ],
    difference: 'Willo focuses on asynchronous interviews in several response formats, with AI summaries and ranking through Willo Intelligence. Beatview also covers the steps before and after the interview: it screens applications against configured criteria, runs structured one-way video interviews with per-dimension scores, and lets agencies share candidates with clients through password-protected links.',
    chooseBeatview: [
      'You want applications screened before candidates are invited to interview.',
      'You are an agency sharing shortlists with clients.',
      'Recruiters need to see the reasoning behind each interview score.',
      'You want a free plan to test on a live role.',
    ],
    keepCompetitor: [
      'You need audio, written or multiple-choice responses as well as video.',
      'Candidates interview in many languages (Willo lists 18+).',
      'You need Enterprise ATS integrations such as Workday or Bullhorn.',
      'Accessibility and anti-cheat controls are key requirements.',
    ],
    buyerQuestions: ['Which integrations are available on Lite versus Enterprise?', 'What happens when a role exceeds 150 assessed candidates on Lite?'],
    ctaHeadline: 'Screen, interview and share in one workflow',
  },
  {
    id: 'vervoe',
    name: 'Vervoe',
    alternativeIds: ['beatview', 'testgorilla', 'criteria-corp', 'harver', 'hirevue', 'vidcruiter', 'willo'],
    overview: 'Vervoe is a skills-assessment platform that tests candidates with job simulations such as coding challenges, spreadsheet tasks, presentations, and written and video responses, then uses AI grading to produce ranked shortlists. It also offers conversational AI screening. Vervoe does not publish pricing; it is provided on request.',
    quickAnswer: 'The best Vervoe alternative depends on whether testing or interviewing sits at the centre of your process. TestGorilla is the closest like-for-like option, with 350+ tests and published plans. Criteria adds cognitive and personality tests such as the CCAT, with video interviews, and Harver suits enterprise volume hiring with game-based assessments. Recruitment agencies that need resume screening, structured one-way video interviews and client sharing can use Beatview. For enterprise assessments plus video interviews, compare HireVue or VidCruiter; for async video, Willo.',
    switchTriggers: [
      { title: 'Published pricing', body: 'Vervoe pricing is available on request, which slows comparison for smaller teams.' },
      { title: 'An interview step', body: 'Skills tests do not replace a structured interview, so teams want both steps in the same workflow.' },
      { title: 'Candidate time', body: 'Job simulations can ask more of candidates than a short first-stage screen; some roles need a lighter first step.' },
      { title: 'Resume stage', body: 'Teams that screen applications first want that evidence alongside assessment results.' },
    ],
    difference: 'Vervoe measures skills through job simulations and AI-graded assessments. Beatview evaluates candidates through application screening and structured one-way video interviews scored per dimension, adds a Work Style assessment, and gives agencies password-protected client sharing. Beatview publishes pricing and has a free plan; Vervoe provides pricing on request.',
    chooseBeatview: [
      'Your first screening steps are the application and a short structured interview.',
      'You share shortlists with clients.',
      'You want published pricing and a free plan.',
      'Recruiters want to watch answers and see the reasoning behind scores.',
    ],
    keepCompetitor: [
      'Role-specific job simulations are your main evidence of ability.',
      'You hire for technical or spreadsheet-heavy roles that need practical tasks.',
      'You want AI-graded skills tests rather than interviews.',
      'Its ATS integration fits your current stack.',
    ],
    buyerQuestions: ['How is AI grading validated for our roles?', 'How long does a typical assessment take candidates?'],
    ctaHeadline: 'Add structured interviews to your screening',
  },
  {
    id: 'hireflix',
    name: 'Hireflix',
    alternativeIds: ['beatview', 'willo', 'interviewer-ai', 'sparkhire', 'vidcruiter', 'testgorilla'],
    overview: 'Hireflix is a one-way video interview tool: candidates record answers to preset questions without downloading an app or registering. Every plan includes unlimited seats, positions and responses, custom branding and automatic transcription. Small costs $75 per month and Medium $150 per month, billed yearly, with custom pricing for agencies and high-volume hiring.',
    quickAnswer: 'The best Hireflix alternative depends on how much you need beyond one-way video. For AI scoring, resume screening and client sharing for agency delivery, consider Beatview. Willo is another async tool that adds audio and written responses plus AI summaries and ranking. Spark Hire adds live interviews and an optional ATS; VidCruiter suits structured enterprise hiring; TestGorilla adds skills tests.',
    switchTriggers: [
      { title: 'Scored answers', body: 'Hireflix lists transcription but not AI scoring of answers; teams screening large volumes want ranked results.' },
      { title: 'Application screening', body: 'Candidates are invited without an application screening step in the same tool.' },
      { title: 'More candidate evidence', body: 'Teams that want work-style or skills evidence alongside interviews add another tool.' },
      { title: 'Pricing by company size', body: 'Plans are priced by company size (under 50 or 50–250 employees), so growing teams change tiers.' },
    ],
    difference: 'Hireflix keeps one-way video interviews simple, with unlimited seats and transcription. Beatview adds the surrounding workflow: it screens applications against configured criteria, scores each interview answer with visible reasoning, ranks completed interviews, adds a Work Style assessment and lets agencies share candidates with clients through password-protected links.',
    chooseBeatview: [
      'You want interview answers scored and ranked, not only recorded.',
      'You want resume screening before the interview invitation.',
      'You share candidates with clients.',
      'You want to start on a free plan.',
    ],
    keepCompetitor: [
      'You only need simple one-way video with unlimited seats.',
      'You want WhatsApp and SMS candidate communications.',
      'Its native integrations (Lever, Greenhouse, Workable, Ashby, BambooHR) match your ATS.',
      'You prefer reviewing every video yourself without AI scoring.',
    ],
    buyerQuestions: ['What does the Custom plan include for recruitment agencies?', 'Can we export or download responses for clients?'],
    ctaHeadline: 'Go beyond recording to scored shortlists',
  },
  {
    id: 'vidcruiter',
    name: 'VidCruiter',
    alternativeIds: ['beatview', 'hirevue', 'talview', 'sparkhire', 'willo', 'hireflix'],
    overview: 'VidCruiter is a structured hiring suite with pre-recorded and live video interviews, self-scheduling, structured interview guides, automated references, skills testing, video proctoring and its own ATS. Its AI scores candidates against rubrics, writes notes and summaries, and flags potential interview fraud. VidCruiter is SOC 2 Type II certified and provides pricing on request.',
    quickAnswer: 'The best VidCruiter alternative depends on how much of its suite you use. For a comparable enterprise platform, compare HireVue. For simpler async video with published pricing, look at Spark Hire, Willo or Hireflix. Recruitment agencies that want resume screening, scored one-way video interviews and password-protected client sharing without an enterprise suite can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'Suite scope', body: 'Teams that only use video screening may not need scheduling, references, proctoring and an ATS.' },
      { title: 'Pricing on request', body: 'VidCruiter pricing is provided through a request form rather than published plans.' },
      { title: 'Agency client review', body: 'Agencies need to share candidates with clients and keep evidence organised per client role.' },
      { title: 'Configuration effort', body: 'Broader suites involve more configuration, so smaller teams compare how much setup a first live role needs.' },
    ],
    difference: 'VidCruiter is a broad structured-hiring suite for large and regulated employers, covering live and pre-recorded interviews, scheduling, references, testing and proctoring. Beatview focuses on recruitment agency delivery: application screening, structured one-way AI video interviews with per-dimension scoring, a Work Style assessment and password-protected client sharing, with published pricing and a free plan.',
    chooseBeatview: [
      'You are an agency that needs protected client sharing and external notes.',
      'One-way structured interviews cover your screening stage.',
      'You want published pricing and a free plan to test on a live role.',
      'You want resume screening and interview scoring in one focused workflow.',
    ],
    keepCompetitor: [
      'You need live interviews, self-scheduling and automated references in one suite.',
      'You hire in public sector, healthcare or education with formal compliance requirements.',
      'Identity verification, proctoring or fraud detection is required.',
      'You want an ATS from the same vendor.',
    ],
    buyerQuestions: ['Which modules are priced separately?', 'What does implementation involve for our team size?'],
    ctaHeadline: 'Try a focused alternative on one live role',
  },
  {
    id: 'paradox',
    name: 'Paradox',
    alternativeIds: ['beatview', 'sapia', 'alex', 'heymilo', 'tenzo', 'hirevue'],
    overview: 'Paradox is a conversational AI platform for high-volume hiring. Its assistant, Olivia, screens applicants against role requirements by chat or text, schedules and reschedules interviews, answers candidate questions in more than 100 languages, and handles offers and onboarding paperwork. Paradox also sells a conversational ATS, and its site now carries “A Workday Company”. It does not publish pricing.',
    quickAnswer: 'The best Paradox alternative depends on how you hire. Employers hiring hundreds of people a year who want a structured AI interview for every applicant can compare Sapia.ai, which runs text-based chat interviews and prices per hire. Staffing firms that want an AI recruiter to call or interview every applicant can compare Alex, HeyMilo and Tenzo. Enterprise teams that want video interviews, assessments and scheduling from one vendor can look at HireVue. Recruitment agencies that want resume screening, structured one-way video interviews and client sharing at a published price can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'A price you can see', body: 'Paradox does not publish prices; its pricing page leads to a demo request, so smaller teams can’t compare cost before a sales process.' },
      { title: 'Agency workflows', body: 'Paradox’s industry pages cover employers only, with no staffing solution, so agencies that present candidates to clients look elsewhere.' },
      { title: 'Scored interview answers', body: 'Chat screening checks requirements and books interviews. Some teams also want structured interview answers they can watch, score and compare.' },
      { title: 'Voice screening', body: 'Paradox describes chat, text and video, but no AI phone interviews, so teams that want every applicant called compare AI voice recruiters.' },
    ],
    difference: 'Paradox automates the conversation around high-volume hiring: chat screening, scheduling, candidate questions, offers and onboarding, mostly for employers hiring frontline staff. Beatview is built for recruitment agencies: it screens each application against the criteria a consultant sets, runs structured one-way video interviews scored answer by answer with the reasoning shown, and shares candidates with clients through password-protected links. Beatview publishes its prices and has a free plan.',
    chooseBeatview: [
      'You are a recruitment agency presenting shortlists to clients.',
      'You want structured video answers you can watch and score, not only chat replies.',
      'Recruiters need to see the reasoning behind each score before deciding.',
      'You want published pricing and a free plan to test on a live role.',
    ],
    keepCompetitor: [
      'You hire hundreds or thousands of frontline or hourly staff a year.',
      'You want scheduling, offers and onboarding handled in the same conversation.',
      'Candidates need answers in many languages; Paradox lists more than 100.',
      'You run Workday or SAP SuccessFactors and want Paradox’s integrations.',
    ],
    buyerQuestions: ['What is pricing based on: hires, locations or employees?', 'Which of our systems have native integrations?'],
    ctaHeadline: 'Try a Paradox alternative built for agencies',
  },
  {
    id: 'interviewer-ai',
    name: 'Interviewer.AI',
    alternativeIds: ['beatview', 'myinterview', 'jobma', 'willo', 'hireflix', 'sparkhire', 'hirevue'],
    overview: 'Interviewer.AI is an AI interview platform. It scores resumes, runs one-way video interviews and, on Professional and above, conversational interviews with an AI avatar, then ranks candidates with score breakdowns and fraud flags. Essential costs $199 a month or $1,990 a year, Professional $399 a month or $3,990 a year, and Enterprise is custom, each with a pool of interview credits. It has a staffing agency page and lets recruiters share candidates with clients by email link.',
    quickAnswer: 'The best Interviewer.AI alternative depends on what you need around the interview. Recruitment agencies that want resume screening, structured one-way video interviews scored answer by answer and client sharing on a per-account price can use Beatview, which has a free plan. For other one-way video tools, compare myInterview, Jobma, Willo and Hireflix. Spark Hire adds live interviews and an optional ATS, and HireVue suits enterprise teams that want video interviews, assessments and scheduling from one vendor.',
    switchTriggers: [
      { title: 'Credits and caps', body: 'Essential includes 50 interview credits and 3 job posts a month, and an avatar interview uses 2 credits, so the cost follows your interview volume.' },
      { title: 'Score detail by plan', body: 'Detailed score breakdowns, transcript summaries and fraud checks are on Professional and above, not on Essential.' },
      { title: 'ATS integrations by plan', body: 'Native ATS integrations need annual Professional with a one-time customisation fee, or Enterprise, and API access is Enterprise only.' },
      { title: 'Live interviews', body: 'Interviewer.AI has no live video interviews on the platform; it points to Google Meet, Teams or Zoom through Calendly.' },
    ],
    difference: 'Interviewer.AI and Beatview both screen resumes, run one-way video interviews and let agencies share candidates with clients. Interviewer.AI meters interviews in credits on plans from $199 a month and adds AI avatar interviews on Professional. Beatview Pro is $219 a month, or $179 a month billed annually, per account for up to 3 active jobs, with structured one-way video interviews scored answer by answer, the reasoning shown for every score, a Work Style assessment and password-protected client links. Beatview also has a free plan for one active job.',
    chooseBeatview: [
      'You want a free plan to test screening on one live role.',
      'Recruiters need to see the reasoning behind each answer’s score.',
      'You want AI video interviews without buying interview credits.',
      'You share candidates through password-protected links with external client notes.',
    ],
    keepCompetitor: [
      'You want candidates to talk to a conversational AI avatar, not only record answers.',
      'You need ID verification and fraud checks such as eye-gaze and lip-sync detection.',
      'Its native integration with Bullhorn, JobAdder or Vincere fits your stack.',
      'Candidates answer in one of its listed languages, such as Arabic, Thai or Vietnamese.',
    ],
    buyerQuestions: ['How many interview credits would our monthly volume use?', 'Which plan includes the native integration for our ATS, and what is the one-time fee?'],
    ctaHeadline: 'Compare Interviewer.AI with Beatview on a live role',
  },
  {
    id: 'talview',
    name: 'Talview',
    alternativeIds: ['beatview', 'hirevue', 'vidcruiter', 'glider-ai', 'interviewer-ai', 'testgorilla', 'sparkhire'],
    overview: 'Talview is an enterprise platform for hiring and exam proctoring. It offers one-way video interviews, live interview rooms with coding and whiteboard tools, an AI interviewer called Ivy, recorded phone screening, AI resume scoring and 2,000+ assessments, with identity verification, face and voice matching and deepfake checks. Talview does not publish prices: quotes are unit-based, and its pricing FAQ cites a typical minimum commitment of about 150 to 200 candidates.',
    quickAnswer: 'The best Talview alternative depends on why you use it. For an enterprise suite with video interviews, an AI interviewer and assessments, compare HireVue or VidCruiter. Staffing firms that need skills validation and proctoring, with a Bullhorn Marketplace listing, can look at Glider AI. For tests alone, TestGorilla publishes its plans. Recruitment agencies that want resume screening, one-way video interviews and client sharing at a published price can compare Beatview and Interviewer.AI; Beatview has a free plan.',
    switchTriggers: [
      { title: 'A minimum commitment', body: 'Talview quotes by unit, and its pricing FAQ cites a typical minimum of about 150 to 200 candidates, which is a lot for a small team testing one role.' },
      { title: 'Agency ATS integrations', body: 'Bullhorn, JobAdder, Vincere, Loxo and Recruit CRM are not on Talview’s integrations page, and Zapier is not mentioned.' },
      { title: 'Proctoring you may not need', body: 'Much of Talview’s depth is in proctoring and identity checks, such as lockdown browsers and secondary cameras, which many first-round screens don’t require.' },
      { title: 'Client delivery', body: 'Talview describes no client portal for agencies; outside reviewers join as guest interviewers from approved domains.' },
    ],
    difference: 'Talview is built for high-stakes hiring and testing, with proctoring, identity verification, an AI interviewer and a large assessment library, sold on unit-based quotes. Beatview is built for recruitment agencies: it screens applications against the criteria a consultant sets, runs structured one-way video interviews scored answer by answer with the reasoning shown, and shares candidates with clients through password-protected links. Beatview publishes its prices and has a free plan.',
    chooseBeatview: [
      'You are an agency sharing shortlists with clients.',
      'You want to start on a free plan, without a minimum commitment.',
      'Recruiters need to see the reasoning behind each score.',
      'One-way structured interviews cover your first screening stage.',
    ],
    keepCompetitor: [
      'You need identity verification, proctoring or deepfake detection.',
      'You run certification exams or coding tests as well as interviews.',
      'You want live interview rooms with a coding editor and whiteboard.',
      'You run Workday, SuccessFactors or iCIMS and want Talview’s integrations.',
    ],
    buyerQuestions: ['What counts as a unit, and what is our minimum commitment?', 'Which features are priced separately from the bundle we need?'],
    ctaHeadline: 'Try a Talview alternative without a minimum',
  },
  {
    id: 'metaview',
    name: 'Metaview',
    alternativeIds: ['beatview', 'brighthire', 'employ-interview-companion', 'screenloop', 'heymilo', 'alex'],
    overview: 'Metaview is an AI recruiting platform built around an interview notetaker. It records video, phone and in-person interviews, with or without a bot, and writes notes and scorecards into the ATS. Separately priced agents add sourcing, application review, async AI voice screening and email outreach. Notetaker Pro costs $60 per user a month, Sourcing $100 or $300 per user a month and Application Review from $150 a month, with free tiers for each; the price of Screening credits is not published.',
    quickAnswer: 'The best Metaview alternative depends on which part of Metaview you use. For notes on live interviews, compare BrightHire, which adds an async AI interviewer and is now part of Zoom; Employ’s AI Interview Companion (formerly Pillar), with live interview guides; and Screenloop, an ATS with a built-in notetaker. For AI screening of every applicant before a call, compare HeyMilo and Alex. Recruitment agencies that want to replace first-round calls with structured one-way video interviews, scored with the reasoning shown and shared with clients, can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'The first call still happens', body: 'A notetaker writes up a call that still has to take place. Teams with high applicant volumes look for a screen that runs before any recruiter time is spent.' },
      { title: 'Screening price not published', body: 'Metaview’s AI Screening agent uses one credit per assessed interview, and the credit price is not published.' },
      { title: 'Agency ATS coverage', body: 'Vincere is not on Metaview’s integrations list, and supported ATS differ by product: the help centre’s Application Review list omits JobAdder and Loxo.' },
      { title: 'Free plan limits', body: 'The free Notetaker plan covers 25 calls per user a month, 60 calls across all free users, and keeps only 14 days of history.' },
    ],
    difference: 'Metaview writes up the interviews your team runs, and adds agents for sourcing, application review and AI voice screening. Beatview replaces the first-round call for agency roles: it screens each application against the criteria a consultant sets, runs a structured one-way video interview, scores each answer with the reasoning shown and shares candidates with clients through password-protected links. The two can work together: Beatview for the first screen, a notetaker for the live interviews that follow.',
    chooseBeatview: [
      'You want to cut first-round screening calls, not just write them up.',
      'You want every applicant to answer the same timed questions on video.',
      'You present shortlists to clients through password-protected links.',
      'You want a free plan to test on one live role.',
    ],
    keepCompetitor: [
      'Your recruiters run many live client and candidate calls that need notes.',
      'You want notes and scorecards pushed to Bullhorn, JobAdder, Loxo or Recruit CRM.',
      'You want AI sourcing and application review from the same vendor.',
      'You need botless recording, or notes from phone and in-person interviews.',
    ],
    buyerQuestions: ['What does a Screening credit cost at our volume?', 'Which of its agents support our ATS?'],
    ctaHeadline: 'Replace the first-round call, not just its notes',
  },
  {
    id: 'brighthire',
    name: 'BrightHire',
    alternativeIds: ['beatview', 'metaview', 'employ-interview-companion', 'screenloop', 'heymilo', 'alex', 'hirevue'],
    overview: 'BrightHire is an interview intelligence platform for in-house talent teams. It records and transcribes live Zoom, Google Meet, Teams, phone and in-person interviews, writes AI notes, fills ATS scorecards in one click and adds interview planning, coaching and analytics. BrightHire Screen adds an async AI interviewer by voice or video, scored against your rubrics. BrightHire says it is now a Zoom company and continues as a standalone brand. It does not publish prices.',
    quickAnswer: 'The best BrightHire alternative depends on which part of BrightHire you use. For notes on live interviews, compare Metaview, which publishes prices and has free tiers; Employ’s AI Interview Companion (formerly Pillar), with live interview guides; and Screenloop, an ATS with a built-in notetaker. For an AI interviewer that screens every applicant, compare HeyMilo and Alex, both built for staffing desks, or HireVue for enterprise video interviews and assessments. Recruitment agencies that want to replace first-round calls with structured one-way video interviews, scored with the reasoning shown and shared with clients, can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'No published prices', body: 'Every BrightHire plan starts with a demo, and its AI screen is priced by interview volume, so smaller teams can’t compare cost before a sales process.' },
      { title: 'Agency ATS coverage', body: 'BrightHire lists Bullhorn among 18 ATS, but not JobAdder, Vincere, Loxo or Recruit CRM, and its AI screen lists Greenhouse, Ashby, Workday and SmartRecruiters.' },
      { title: 'AI screen languages', body: 'BrightHire Screen runs in English, Latin American Spanish and Brazilian Portuguese, so teams hiring in other languages compare alternatives.' },
      { title: 'The first call still happens', body: 'Interview notes write up a call that still has to take place. Teams with high applicant volumes look for a screen that runs before any recruiter time is spent.' },
    ],
    difference: 'BrightHire helps in-house teams run and write up structured live interviews, and adds an AI interviewer for the first round. Beatview is built for recruitment agencies: it screens each application against the criteria a consultant sets, runs a structured one-way video interview, scores each answer with the reasoning shown and shares candidates with clients through password-protected links. Beatview publishes its prices, per account, and has a free plan.',
    chooseBeatview: [
      'You are a recruitment agency presenting shortlists to clients.',
      'You want application screening and the first interview in one workflow.',
      'You want published pricing and a free plan to test on a live role.',
      'Recruiters need to see the reasoning behind each answer’s score.',
    ],
    keepCompetitor: [
      'Your team runs many live interviews that need notes, scorecards and coaching.',
      'You run Greenhouse, Ashby, Workday or SmartRecruiters and want its AI screen there.',
      'You want fraud signals across AI screens and live interviews.',
      'You use Zoom and want interview intelligence from a Zoom company.',
    ],
    buyerQuestions: ['How is the AI screen priced at our interview volume?', 'Which of its products support our ATS?'],
    ctaHeadline: 'Try a BrightHire alternative built for agencies',
  },
  {
    id: 'myinterview',
    name: 'myInterview',
    alternativeIds: ['beatview', 'interviewer-ai', 'jobma', 'willo', 'hireflix', 'sparkhire'],
    overview: 'myInterview was a one-way video interview tool. Radancy announced its acquisition in September 2025, and myinterview.com now redirects to Radancy’s Screening & Scheduling product, which combines conversational AI screening, optional one-way video answers scored against a recruiter’s scorecard, and AI interview scheduling. Prices are not published: the old pricing page now returns an error, and the service is sold through a Radancy order form.',
    quickAnswer: 'If you used myInterview for one-way video interviews and want a standalone tool, compare Interviewer.AI, which publishes plans from $199 a month and has a staffing page; Jobma, which adds live interviews and skills tests and lists integrations for agency ATS; and Willo or Hireflix for simple async video at a published price. Spark Hire adds live interviews and an optional ATS. Recruitment agencies that want resume screening, structured one-way video interviews scored with the reasoning shown, and password-protected client sharing can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'Now part of Radancy', body: 'myinterview.com redirects to Radancy, and the product is sold as part of Radancy’s Talent Acquisition Cloud through an order form.' },
      { title: 'No published price', body: 'The old myInterview pricing page now returns an error, and Radancy publishes no price for Screening & Scheduling.' },
      { title: 'Agency ATS integrations', body: 'Radancy’s integration list names enterprise systems such as Workday and Greenhouse; Bullhorn, JobAdder, Vincere, Loxo and Recruit CRM are not named.' },
      { title: 'Features behind add-ons', body: 'Video questions, CSV export, smart shortlisting and premium client sharing depend on plans or add-ons whose prices are not public.' },
    ],
    difference: 'myInterview, now Radancy Screening & Scheduling, is an enterprise module for AI screening, optional one-way video and interview scheduling, bought with Radancy’s wider platform. Beatview is a standalone tool for recruitment agencies: it screens each application against the criteria a consultant sets, runs structured one-way video interviews scored answer by answer with the reasoning shown, and shares candidates with clients through password-protected links. Beatview publishes its prices and has a free plan.',
    chooseBeatview: [
      'You want a standalone screening tool, not a module of a larger platform.',
      'You want to see prices and start free before talking to sales.',
      'Recruiters need to see the reasoning behind each score.',
      'You share shortlists with clients through password-protected links.',
    ],
    keepCompetitor: [
      'You already use Radancy’s Talent Acquisition Cloud.',
      'You want AI scheduling for panel and multi-stage interviews.',
      'Candidates should be able to answer by voice or text instead of video.',
      'You need separate branding for many brands or clients in one account.',
    ],
    buyerQuestions: ['Which Radancy plan or add-on includes the features we used in myInterview?', 'What happens to our existing myInterview data and contract?'],
    ctaHeadline: 'Find a myInterview alternative you can try today',
  },
  {
    id: 'jobma',
    name: 'Jobma',
    alternativeIds: ['beatview', 'interviewer-ai', 'sparkhire', 'willo', 'hireflix', 'vidcruiter', 'testgorilla'],
    overview: 'Jobma is a video interviewing and assessment platform founded in 2012. Every plan includes unlimited one-way video interviews; higher plans add live and panel interviews, autonomous AI interviews, AI scoring, proctoring, and skill, coding, game-based and language tests. It has a staffing solution with client access and white-labelling, and integration pages for Bullhorn, JobAdder, Vincere, Loxo and Recruit CRM. Its four plans publish detailed limits, but every price is on request.',
    quickAnswer: 'The best Jobma alternative depends on whether you need prices up front and how much of Jobma you use. Interviewer.AI is the closest like-for-like option with published plans, from $199 a month, plus a staffing page and client sharing. For async video at a published price, compare Spark Hire, Willo or Hireflix; for an enterprise suite with live interviews and references, VidCruiter; for tests alone, TestGorilla. Recruitment agencies that want resume screening, structured one-way video interviews scored with the reasoning shown, and password-protected client sharing can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'Prices on request', body: 'All four Jobma plans say “Request Pricing”, and no free plan or trial is published.' },
      { title: 'Starter limits', body: 'Starter allows 1 user, 5 active positions, no bulk invitations and 1 ATS integration, with no AI scoring or resume screening.' },
      { title: 'Features by tier', body: 'Autonomous AI interviews, panel interviews and white-labelling need Professional; Zapier, the REST API and SSO need Enterprise.' },
      { title: 'Live sessions', body: 'Growth includes one live video session a month; unlimited live interviews start on Professional.' },
    ],
    difference: 'Jobma combines one-way, live and AI-led video interviews with a broad set of skills tests, and prices every plan on request. Beatview focuses on the agency first screen: it scores each application against the criteria a consultant sets, runs structured one-way video interviews scored answer by answer with the reasoning shown, adds a Work Style assessment, and shares candidates with clients through password-protected links. Beatview publishes its prices and has a free plan.',
    chooseBeatview: [
      'You want published prices and a free plan to test on a live role.',
      'You want resume screening included from the free plan.',
      'One-way structured interviews cover your first screening stage.',
      'Recruiters need to see the reasoning behind each answer’s score.',
    ],
    keepCompetitor: [
      'You need live and panel video interviews in the same tool.',
      'Coding, game-based or language tests are part of your process.',
      'You want full white-labelling for each client.',
      'Its native integration with your ATS, such as Bullhorn or Vincere, is a must.',
    ],
    buyerQuestions: ['What does each plan cost for our number of users and active positions?', 'Which ATS fields sync, and on which plan?'],
    ctaHeadline: 'Compare Jobma with Beatview on a live role',
  },
  {
    id: 'harver',
    name: 'Harver',
    alternativeIds: ['beatview', 'criteria-corp', 'hirevue', 'sapia', 'paradox', 'testgorilla', 'vervoe'],
    overview: 'Harver is a talent assessment platform for volume hiring. It offers over 450 assessment options, including pymetrics game-based assessments and cognitive and situational judgement tests, plus one-way and live video interviews, interview scheduling and Checkster reference checks. It says it helps BPOs, contact centres and retail organisations hire at speed. Recruiters rate Harver’s video interview answers themselves; the AI does not. Harver does not publish pricing, and in September 2026 it announced the acquisition of Symphony Talent, a recruitment marketing company.',
    quickAnswer: 'The best Harver alternative depends on what you need beyond assessments. Teams that want cognitive, personality and skills tests with video interviews can compare Criteria, which offers a 14-day free trial, or TestGorilla and Vervoe for skills testing; TestGorilla publishes prices and has a free plan. Enterprise volume hirers can compare HireVue for assessments and video interviews, Sapia.ai for a text-based AI interview for every applicant, and Paradox for screening and scheduling by chat. Recruitment agencies that want resume screening, structured one-way video interviews scored answer by answer and client sharing at a published price can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'No published pricing', body: 'Harver has no pricing page; deals start with a demo and a proposal from its sales team, so smaller teams can’t compare cost up front.' },
      { title: 'Video answers scored by hand', body: 'Harver’s help centre says video interview answers are not rated by AI and must be scored manually by recruiters, which takes time at volume.' },
      { title: 'Built for enterprise volume', body: 'Harver positions itself for BPOs, contact centres, retail and other large frontline employers, and its old staffing page now redirects to a general professional services page.' },
      { title: 'Agency ATS coverage', body: 'Harver’s integrations page and help centre don’t list Bullhorn, JobAdder or Vincere, the systems many recruitment agencies run on.' },
    ],
    difference: 'Harver predicts fit for high-volume roles with validated assessments, and adds video interviews, scheduling and reference checks around them for large employers. Beatview is built for recruitment agencies: it screens each resume against the criteria a consultant sets, runs a structured one-way video interview, scores every answer with the reasoning shown, and shares candidates with clients through password-protected links. Beatview publishes its prices, per account, and has a free plan.',
    chooseBeatview: [
      'You are a recruitment agency presenting shortlists to clients.',
      'You want video answers scored for you, with the reasoning shown.',
      'You want each resume screened before the interview, in the same tool.',
      'You want published pricing and a free plan to test on a live role.',
    ],
    keepCompetitor: [
      'You hire hundreds or thousands of contact centre, retail or hourly staff a year.',
      'You want game-based and situational judgement assessments matched to job profiles.',
      'You want reference checks and interview scheduling from the same vendor.',
      'You run Workday, iCIMS or UKG and want Harver’s integrations.',
    ],
    buyerQuestions: ['What would a proposal cost at our annual hiring volume?', 'Are integration setup or subscription fees charged separately?'],
    ctaHeadline: 'Try a Harver alternative built for agencies',
  },
  {
    id: 'criteria-corp',
    name: 'Criteria Corp',
    alternativeIds: ['beatview', 'testgorilla', 'vervoe', 'harver', 'hirevue', 'talview', 'sparkhire'],
    overview: 'Criteria is a pre-employment assessment and interviewing platform, sold as the Talent Signal platform. It offers cognitive aptitude tests such as the CCAT, plus personality, emotional intelligence, risk, skills and game-based tests, and timed one-way and live video interviews. An AI Scoring add-on rates one-way interview answers from their transcripts, and an AI Interview Agent is marked coming soon. Criteria sells three plans, Assess, Interview and Hire, on a quoted subscription built around headcount, with a 14-day free trial for employers.',
    quickAnswer: 'The best Criteria alternative depends on what you test for. Teams that want a large skills test library can compare TestGorilla, which publishes prices and has a free plan, and Vervoe, which builds role-specific skills tests and job simulations. Enterprise volume hirers can compare Harver, with pymetrics game-based assessments, and HireVue, for assessments and video interviews from one vendor, or Talview for proctored assessments and AI interviews. Spark Hire suits teams that mainly want one-way and live video interviews at a published price. Recruitment agencies that want resume screening, structured one-way video interviews scored answer by answer and client sharing can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'Agency use needs consent', body: 'Criteria’s terms say a recruitment agency must declare itself in writing before buying and get written consent to use the service for clients, with agency pricing set by agency size, roles and number of clients.' },
      { title: 'No resume screening', body: 'Criteria positions its tests as going beyond the resume and has no resume screening product, so applications still need reviewing elsewhere.' },
      { title: 'AI as paid add-ons', body: 'AI Scoring for one-way interviews is a paid add-on, and the AI Interview Agent, which would run voice interviews, is marked coming soon.' },
      { title: 'Quoted pricing, no refunds', body: 'Prices are quoted around headcount, and Criteria’s terms say orders are non-cancellable and fees are not refundable.' },
    ],
    difference: 'Criteria measures how candidates think and work, through validated cognitive, personality and skills tests, and adds video interviews around them. Beatview is built for recruitment agencies: it screens each resume against the criteria a consultant sets, runs a structured one-way video interview with every answer scored and the reasoning shown, adds a Work Style assessment, and shares candidates with clients through password-protected links. Beatview publishes its prices, per account, and has a free plan.',
    chooseBeatview: [
      'You are a recruitment agency presenting shortlists to clients.',
      'You want each resume screened before the interview, in the same tool.',
      'You want AI-scored video interviews included in the plan, not as an add-on.',
      'You want published pricing and a free plan to test on a live role.',
    ],
    keepCompetitor: [
      'You hire for roles where cognitive aptitude tests such as the CCAT predict performance.',
      'You want personality, emotional intelligence, risk and skills tests from one vendor.',
      'You want live and one-way video interviews alongside your tests.',
      'You run JobAdder or iCIMS, where its integration is included in every subscription.',
    ],
    buyerQuestions: ['Do we need agency consent and agency pricing under its terms?', 'Which add-ons, such as AI Scoring or a Bullhorn integration, would we need?'],
    ctaHeadline: 'Try a Criteria alternative built for agencies',
  },
  {
    id: 'glider-ai',
    name: 'Glider AI',
    alternativeIds: ['beatview', 'alex', 'heymilo', 'tenzo', 'talview', 'interviewer-ai', 'hirevue'],
    overview: 'Glider AI is a skills validation and AI interviewing platform. Its AI Recruiter combines agents for chat and phone screening, autonomous AI video interviews, one-way video, skills and coding assessments, proctoring and ID verification. It sells to staffing firms, promising proof of skills to present to clients, integrates with Bullhorn and VMS platforms such as Beeline and Magnit, and says it is built for enterprise hiring teams. Findem announced in March 2026 that it is acquiring Glider AI, which will keep its brand. Glider does not publish pricing.',
    quickAnswer: 'The best Glider AI alternative depends on which part of Glider you use. Staffing firms that want an AI recruiter to call or interview every applicant can compare Alex, HeyMilo and Tenzo. Teams that need proctored assessments, identity checks and AI interviews from one vendor can compare Talview and HireVue. Interviewer.AI offers AI video interviews with resume scoring and client sharing at published prices. Recruitment agencies that want resume screening, structured one-way video interviews scored answer by answer and client sharing at a published price can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'No published pricing', body: 'Glider’s pricing and free-trial pages redirect to a demo request, and its interview software is sold on a licence or through an enterprise hiring programme.' },
      { title: 'Ownership change', body: 'Findem announced in March 2026 that it is acquiring Glider AI. Glider will keep its brand, so buyers may want to confirm how contracts and the roadmap change.' },
      { title: 'Candidate steps', body: 'Glider’s help centre says sharing the screen is a mandatory step before its AI interview starts, which adds a step for every candidate.' },
      { title: 'Agency ATS coverage', body: 'Glider lists Bullhorn, Ceipal and JobDiva among its integrations, but not JobAdder or Vincere.' },
    ],
    difference: 'Glider AI validates skills with AI interviews, proctored assessments and identity checks, sold to staffing firms and enterprise hiring teams through a demo. Beatview is built for recruitment agencies that want a simpler first screen: it screens each resume against the criteria a consultant sets, runs a structured one-way video interview, scores each answer with the reasoning shown, and shares candidates with clients through password-protected links. Beatview publishes its prices, per account, and has a free plan.',
    chooseBeatview: [
      'You want published pricing and a free plan to test on a live role.',
      'You want each resume screened before the interview, in the same tool.',
      'You share candidates through password-protected links with external client notes.',
      'You want a structured video interview without a mandatory screen share.',
    ],
    keepCompetitor: [
      'You need proctored coding and skills assessments with ID verification.',
      'You want an AI to phone-screen applicants around the clock.',
      'You supply contingent workers through VMS platforms such as Beeline or Magnit.',
      'You run Bullhorn, Ceipal or JobDiva and want Glider’s integrations.',
    ],
    buyerQuestions: ['How will Findem’s acquisition affect our contract and roadmap?', 'Which products are in the licence, and how is usage counted?'],
    ctaHeadline: 'Try a Glider AI alternative built for agencies',
  },
  {
    id: 'sapia',
    name: 'Sapia.ai',
    alternativeIds: ['beatview', 'paradox', 'harver', 'hirevue', 'heymilo', 'alex', 'interviewer-ai'],
    overview: 'Sapia.ai is an AI interview platform for enterprise volume hiring. Every applicant takes a short, structured, text-only chat interview; its AI ranks candidates with explainable scores and sends each one personalised insights. Top applicants can move on to a one-way video interview that the hiring team rates, as the AI does not score it. Sapia.ai prices per hire with unlimited interviews, does not publish its rate and says it is designed for companies that hire more than 500 people a year.',
    quickAnswer: 'The best Sapia.ai alternative depends on how you want candidates to answer. Employers hiring frontline staff at volume who want screening and scheduling by chat or text can compare Paradox. Teams that want assessments and video interviews from one enterprise vendor can compare Harver and HireVue. Staffing desks that want an AI recruiter to call or video-interview every applicant can compare HeyMilo and Alex, and Interviewer.AI offers AI video interviews at published prices. Recruitment agencies that want resume screening, structured one-way video interviews scored answer by answer and client sharing at a published price can use Beatview, which has a free plan.',
    switchTriggers: [
      { title: 'Built for 500+ hires a year', body: 'Sapia.ai says its technology is designed for companies that hire more than 500 people a year, so agencies running many smaller searches may not fit its model.' },
      { title: 'Text answers only', body: 'Its AI scores written chat answers only. The video interview that follows is rated by your team, so spoken answers aren’t scored for you.' },
      { title: 'No CV screening', body: 'Sapia.ai replaces the CV with the chat interview, so teams that want each resume checked against the role need another tool.' },
      { title: 'Price on request', body: 'Pricing is per hire at a tailored rate that is not published, and custom integrations may add fixed costs.' },
    ],
    difference: 'Sapia.ai replaces the CV with a text chat interview for every applicant, built for employers hiring hundreds or thousands of people a year. Beatview is built for recruitment agencies: it screens each resume against the criteria a consultant sets, runs a structured one-way video interview, scores each answer with the reasoning shown and shares candidates with clients through password-protected links. Beatview publishes its prices, per account, and has a free plan.',
    chooseBeatview: [
      'You are a recruitment agency presenting shortlists to clients.',
      'You want each resume screened against the role, not replaced by a chat interview.',
      'You want video answers scored with the reasoning shown, not only rated by hand.',
      'You want published pricing and a free plan to test on a live role.',
    ],
    keepCompetitor: [
      'You hire more than 500 people a year, mostly into frontline roles.',
      'You want every applicant to take a short text interview on their phone, with no camera.',
      'You want each candidate to receive personalised insights after the interview.',
      'You run Workday, SAP SuccessFactors, SmartRecruiters or iCIMS and want its integrations.',
    ],
    buyerQuestions: ['What would our cost per hire be at our annual volume?', 'Does our ATS need a custom integration, and what fixed cost would that add?'],
    ctaHeadline: 'Try a Sapia.ai alternative built for agencies',
  },
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const dateLabel = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day} ${MONTH_NAMES[month - 1]} ${year}`;
};

export const getCompetitorBySlug = (slug: string): CompetitorPage | undefined => competitors.find(c => c.id === slug);

export const getPlatform = (id: string): Platform => {
  const platform = PLATFORMS[id];
  if (!platform) throw new Error(`Unknown platform: ${id}`);
  return platform;
};

export const getAlternatives = (page: CompetitorPage): Platform[] => page.alternativeIds.map(getPlatform);

/** When the vendor pages behind one comparison were read: the latest date, and "on …" or "between … and …". */
export function getPageReview(page: CompetitorPage) {
  const dates = [getPlatform(page.id), ...getAlternatives(page)].filter(p => !p.isBeatview).map(p => p.reviewed ?? ALTERNATIVES_REVIEWED).sort();
  const first = dates[0], last = dates[dates.length - 1];
  return { date: last, label: dateLabel(last), when: first === last ? `on ${dateLabel(first)}` : `between ${dateLabel(first)} and ${dateLabel(last)}` };
}

const joinNames = (names: string[]) => (names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

export const alternativesUrl = (page?: CompetitorPage) => (page ? `${SITE}/alternatives/${page.id}` : `${SITE}/alternatives`);

export function getPageSeo(page: CompetitorPage) {
  const alternatives = getAlternatives(page);
  const others = alternatives.filter(p => !p.isBeatview).map(p => p.name);
  return {
    title: `${alternatives.length} Best ${page.name} Alternatives & Competitors (2026)`,
    description: `Compare ${alternatives.length} ${page.name} alternatives and competitors, including ${others.slice(0, 2).join(', ')} and Beatview, on interview format, AI scoring and pricing.`,
    intro: `Compare ${page.name} with ${joinNames(alternatives.map(p => p.name))} by interview format, AI capabilities, integrations and published pricing, with a first-party source for every competitor claim.`,
  };
}

export function getPageFaqs(page: CompetitorPage): Faq[] {
  const competitor = getPlatform(page.id);
  const alternatives = getAlternatives(page);
  const free = alternatives.filter(p => p.freeNote).map(p => p.freeNote as string);
  return [
    { question: `What is the best ${page.name} alternative?`, answer: page.quickAnswer },
    { question: `How much does ${page.name} cost?`, answer: `${competitor.facts.pricing}. This is based on ${page.name}’s own website, reviewed on ${dateLabel(competitor.reviewed ?? ALTERNATIVES_REVIEWED)}; confirm current terms with ${page.name}.` },
    { question: `Is there a free ${page.name} alternative?`, answer: `Yes. ${free.join('. ')}.` },
    { question: `Who are ${page.name}’s main competitors?`, answer: `Common ${page.name} competitors include ${joinNames(alternatives.map(p => `${p.name} (${p.category.toLowerCase()})`))}. The right choice depends on whether you need video interviews, skills testing or an agency screening workflow.` },
    { question: `How is Beatview different from ${page.name}?`, answer: page.difference },
  ];
}

/* ───────────── Hub ───────────── */

/** The most recent date any compared vendor's pages were read, across every comparison. */
export function getHubReview() {
  const last = competitors.map(page => getPageReview(page).date).sort().pop() ?? ALTERNATIVES_REVIEWED;
  return { date: last, label: dateLabel(last) };
}

/** "HireVue, Spark Hire … and Jobma", in hub order. */
export const HUB_PLATFORM_NAMES = joinNames(competitors.map(c => c.name));

export const HUB_SEO = {
  title: 'HireVue, Metaview & Spark Hire Alternatives (2026) Compared',
  description: `Sourced comparisons of ${competitors.length} hiring platforms, including HireVue, BrightHire, Criteria, Metaview and Spark Hire, by interview format, AI and pricing.`,
};

export const HUB_QUICK_ANSWER = 'Hiring platforms split into five groups. Enterprise suites (HireVue, VidCruiter, Talview) combine interviews, assessments and scheduling on quoted pricing. Async video tools (Spark Hire, Willo, Hireflix, Interviewer.AI, Jobma and myInterview, now part of Radancy) record one-way answers, and most of them publish prices. Assessment platforms (TestGorilla, Vervoe, Criteria, Harver) centre on skills, cognitive and behavioural tests. Interview notetakers such as Metaview and BrightHire write up live calls. AI screening tools question every applicant: Paradox and Sapia.ai by chat, Glider AI by phone and video with proctored skills tests. Beatview is built for recruitment agencies, combining resume screening, one-way AI video interviews and client sharing.';

export const HUB_CRITERIA = [
  { title: 'Interview format', description: 'One-way video, live video, or audio and written answers. Match the format to the stage you are replacing.' },
  { title: 'Evidence behind AI scores', description: 'Check whether reviewers can see why a candidate scored as they did, and that people make the decision.' },
  { title: 'Pricing model', description: 'Per role, per seat, credits or quote. Model the cost at your real hiring volume before comparing.' },
  { title: 'Workflow coverage', description: 'Resume screening, interviews, assessments and client review in one place, or several tools to connect.' },
  { title: 'Integrations by plan', description: 'Confirm your ATS is supported natively and which plan unlocks integrations, API and webhooks.' },
  { title: 'Candidate notice and data', description: 'Review consent, recording retention and where candidate data is processed before rollout.' },
];

export const HUB_FAQS: Faq[] = [
  { question: 'What is one-way video interview software?', answer: 'One-way (asynchronous) video interview software sends candidates a set of pre-written questions that they answer on camera in their own time, usually with a time limit per answer. Reviewers watch the recordings later, so no interview has to be scheduled. Tools such as Beatview, Spark Hire, Willo and Hireflix support one-way interviews; some also score or summarise answers with AI.' },
  { question: 'Which hiring platforms publish their pricing?', answer: `As of ${getHubReview().label}, Beatview, Spark Hire, TestGorilla, Willo, Hireflix, Interviewer.AI and Metaview publish prices on their websites. HireVue, VidCruiter, Vervoe, Talview, Jobma, Paradox, BrightHire, Harver, Criteria, Glider AI and myInterview (now part of Radancy) provide pricing on request, and Sapia.ai quotes a price per hire.` },
  { question: 'Which hiring platforms have a free plan or trial?', answer: 'Beatview has a Free plan with one active job and unlimited candidates for that job (AI video interviews are on Pro). TestGorilla has a Free plan with 10 credits a month, and Metaview has free notetaker and application review tiers. Hireflix offers a free first month, Interviewer.AI a 14-day trial, and Criteria a 14-day trial for employers.' },
  { question: 'How were these comparisons researched?', answer: `Beatview reviewed each vendor’s own pricing and product pages between ${ALTERNATIVES_REVIEWED_LABEL} and ${getHubReview().label}, and links every source on the comparison page. Where a vendor does not publish a detail, the page says so instead of guessing. Beatview publishes these pages about its own market, so confirm current terms with each vendor before buying.` },
];

/* ───────────── Structured data (shared by the SPA and the crawler renderer) ───────────── */

const publisher = { '@type': 'Organization', '@id': `${SITE}/#organization`, name: 'Beatview', url: SITE };

const faqSchema = (faqs: Faq[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
});

export function buildAlternativeSchemas(page: CompetitorPage, image = `${SITE}/og-image.png`): object[] {
  const url = alternativesUrl(page);
  const seo = getPageSeo(page);
  const competitor = getPlatform(page.id);
  const itemList = {
    '@type': 'ItemList',
    name: `${page.name} alternatives`,
    numberOfItems: page.alternativeIds.length,
    itemListElement: getAlternatives(page).map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: { '@type': 'SoftwareApplication', name: p.name, url: p.website, applicationCategory: 'BusinessApplication', description: p.bestFor },
    })),
  };
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
        { '@type': 'ListItem', position: 2, name: 'Alternatives', item: alternativesUrl() },
        { '@type': 'ListItem', position: 3, name: `${page.name} Alternatives`, item: url },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: seo.title,
      description: seo.description,
      url,
      mainEntityOfPage: url,
      datePublished: ALTERNATIVES_FIRST_PUBLISHED,
      dateModified: getPageReview(page).date,
      image,
      author: publisher,
      publisher,
      about: { '@type': 'SoftwareApplication', name: competitor.name, url: competitor.website, applicationCategory: 'BusinessApplication' },
      mainEntity: itemList,
    },
    faqSchema(getPageFaqs(page)),
  ];
}

export function buildHubSchemas(): object[] {
  const url = alternativesUrl();
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
        { '@type': 'ListItem', position: 2, name: 'Alternatives', item: url },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: HUB_SEO.title,
      description: HUB_SEO.description,
      url,
      dateModified: getHubReview().date,
      publisher,
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: competitors.length,
        itemListElement: competitors.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: `${c.name} alternatives`, url: alternativesUrl(c) })),
      },
    },
    faqSchema(HUB_FAQS),
  ];
}
